import React, { useState, useEffect } from 'react';
import { Users, Plus, Key, X, AlertCircle, ChevronRight, Search, RefreshCw, UserX, Shield, Crown, ShieldCheck } from 'lucide-react';
import {
  fetchTeamMembers,
  createTeamMember,
  updateRole,
  resetPassword,
  deleteUser,
  fetchPermissions,
  savePermissions,
  TeamMember,
} from '../utils/iamApi';
import { AuthUser } from '../utils/authApi';
import { ROLE_PERMISSIONS, Role, ModuleId, ModuleStatus, getPermissions } from '../utils/permissions';
import { COUNTRY_CODES, POPULAR_COUNTRIES } from '../utils/countryCodes';
import { getCountryCurrency } from '../utils/countryCurrency';
import { CURRENCY_MAP } from '../utils/currency';
import StatePicker from './StatePicker';
import { getDefaultSchemeForCountryAndRegion } from '../utils/taxSchemes';
import { inspectTaxId, TaxIdInfo } from '../utils/taxId';

// Master set of all countries that have sub-national tax regions, states, or
// exempt zones (matches Product Catalog / Profile / mobile app) — used as a
// fallback alongside scheme.region_based so this screen shows the
// State/Province picker for the same countries everywhere else does.
const REGIONAL_TAX_COUNTRIES = new Set([
  'IN', 'US', 'CA', 'AU', 'BR', 'AR', 'PK', 'ES', 'PT', 'CH', 'NG',
  'MX', 'CO', 'ID', 'PE', 'MY', 'PH', 'CL', 'CN', 'RU', 'EC', 'VN',
  'DE', 'IT', 'FI', 'NO', 'DK', 'AT', 'BA', 'KR', 'TH', 'IL',
]);

// Currency codes + names sourced from currency.ts
const ALL_CURRENCIES = Object.keys(CURRENCY_MAP).sort();

// ISO 3166-1 alpha-2 → full country name. Neither countryCodes.ts (phone codes)
// nor countryCurrency.ts (currency codes) carries full names, so this is added
// here purely for display purposes in the dropdown.
const COUNTRY_NAMES: Record<string, string> = {
  AF: 'Afghanistan', AL: 'Albania', DZ: 'Algeria', AS: 'American Samoa', AD: 'Andorra',
  AO: 'Angola', AI: 'Anguilla', AQ: 'Antarctica', AG: 'Antigua and Barbuda', AR: 'Argentina',
  AM: 'Armenia', AW: 'Aruba', AU: 'Australia', AT: 'Austria', AZ: 'Azerbaijan',
  BS: 'Bahamas', BH: 'Bahrain', BD: 'Bangladesh', BB: 'Barbados', BY: 'Belarus',
  BE: 'Belgium', BZ: 'Belize', BJ: 'Benin', BM: 'Bermuda', BT: 'Bhutan',
  BO: 'Bolivia', BA: 'Bosnia and Herzegovina', BW: 'Botswana', BR: 'Brazil',
  IO: 'British Indian Ocean Territory', VG: 'British Virgin Islands', BN: 'Brunei',
  BG: 'Bulgaria', BF: 'Burkina Faso', BI: 'Burundi', KH: 'Cambodia', CM: 'Cameroon',
  CA: 'Canada', CV: 'Cape Verde', KY: 'Cayman Islands', CF: 'Central African Republic',
  TD: 'Chad', CL: 'Chile', CN: 'China', CX: 'Christmas Island', CC: 'Cocos Islands',
  CO: 'Colombia', KM: 'Comoros', CK: 'Cook Islands', CR: 'Costa Rica', HR: 'Croatia',
  CU: 'Cuba', CW: 'Curacao', CY: 'Cyprus', CZ: 'Czech Republic', CD: 'DR Congo',
  DK: 'Denmark', DJ: 'Djibouti', DM: 'Dominica', DO: 'Dominican Republic', EC: 'Ecuador',
  EG: 'Egypt', SV: 'El Salvador', GQ: 'Equatorial Guinea', ER: 'Eritrea', EE: 'Estonia',
  ET: 'Ethiopia', FK: 'Falkland Islands', FO: 'Faroe Islands', FJ: 'Fiji', FI: 'Finland',
  FR: 'France', GF: 'French Guiana', PF: 'French Polynesia', GA: 'Gabon', GM: 'Gambia',
  GE: 'Georgia', DE: 'Germany', GH: 'Ghana', GI: 'Gibraltar', GR: 'Greece',
  GL: 'Greenland', GD: 'Grenada', GP: 'Guadeloupe', GU: 'Guam', GT: 'Guatemala',
  GG: 'Guernsey', GN: 'Guinea', GW: 'Guinea-Bissau', GY: 'Guyana', HT: 'Haiti',
  HN: 'Honduras', HK: 'Hong Kong', HU: 'Hungary', IS: 'Iceland', IN: 'India',
  ID: 'Indonesia', IR: 'Iran', IQ: 'Iraq', IE: 'Ireland', IM: 'Isle of Man',
  IL: 'Israel', IT: 'Italy', CI: 'Ivory Coast', JM: 'Jamaica', JP: 'Japan',
  JE: 'Jersey', JO: 'Jordan', KZ: 'Kazakhstan', KE: 'Kenya', KI: 'Kiribati',
  XK: 'Kosovo', KW: 'Kuwait', KG: 'Kyrgyzstan', LA: 'Laos', LV: 'Latvia',
  LB: 'Lebanon', LS: 'Lesotho', LR: 'Liberia', LY: 'Libya', LI: 'Liechtenstein',
  LT: 'Lithuania', LU: 'Luxembourg', MO: 'Macau', MK: 'North Macedonia', MG: 'Madagascar',
  MW: 'Malawi', MY: 'Malaysia', MV: 'Maldives', ML: 'Mali', MT: 'Malta',
  MH: 'Marshall Islands', MQ: 'Martinique', MR: 'Mauritania', MU: 'Mauritius', YT: 'Mayotte',
  MX: 'Mexico', FM: 'Micronesia', MD: 'Moldova', MC: 'Monaco', MN: 'Mongolia',
  ME: 'Montenegro', MS: 'Montserrat', MA: 'Morocco', MZ: 'Mozambique', MM: 'Myanmar',
  NA: 'Namibia', NR: 'Nauru', NP: 'Nepal', NL: 'Netherlands', NC: 'New Caledonia',
  NZ: 'New Zealand', NI: 'Nicaragua', NE: 'Niger', NG: 'Nigeria', NU: 'Niue',
  NF: 'Norfolk Island', KP: 'North Korea', MP: 'Northern Mariana Islands', NO: 'Norway',
  OM: 'Oman', PK: 'Pakistan', PW: 'Palau', PS: 'Palestine', PA: 'Panama',
  PG: 'Papua New Guinea', PY: 'Paraguay', PE: 'Peru', PH: 'Philippines',
  PN: 'Pitcairn Islands', PL: 'Poland', PT: 'Portugal', PR: 'Puerto Rico', QA: 'Qatar',
  CG: 'Republic of the Congo', RE: 'Reunion', RO: 'Romania', RU: 'Russia', RW: 'Rwanda',
  BL: 'Saint Barthelemy', SH: 'Saint Helena', KN: 'Saint Kitts and Nevis', LC: 'Saint Lucia',
  MF: 'Saint Martin', PM: 'Saint Pierre and Miquelon', VC: 'Saint Vincent and the Grenadines',
  WS: 'Samoa', SM: 'San Marino', ST: 'Sao Tome and Principe', SA: 'Saudi Arabia',
  SN: 'Senegal', RS: 'Serbia', SC: 'Seychelles', SL: 'Sierra Leone', SG: 'Singapore',
  SX: 'Sint Maarten', SK: 'Slovakia', SI: 'Slovenia', SB: 'Solomon Islands', SO: 'Somalia',
  ZA: 'South Africa', KR: 'South Korea', SS: 'South Sudan', ES: 'Spain', LK: 'Sri Lanka',
  SD: 'Sudan', SR: 'Suriname', SJ: 'Svalbard and Jan Mayen', SZ: 'Eswatini', SE: 'Sweden',
  CH: 'Switzerland', SY: 'Syria', TW: 'Taiwan', TJ: 'Tajikistan', TZ: 'Tanzania',
  TH: 'Thailand', TL: 'Timor-Leste', TG: 'Togo', TK: 'Tokelau', TO: 'Tonga',
  TT: 'Trinidad and Tobago', TN: 'Tunisia', TR: 'Turkey', TM: 'Turkmenistan',
  TC: 'Turks and Caicos Islands', TV: 'Tuvalu', VI: 'U.S. Virgin Islands', UG: 'Uganda',
  UA: 'Ukraine', AE: 'United Arab Emirates', GB: 'United Kingdom', US: 'United States',
  UY: 'Uruguay', UZ: 'Uzbekistan', VU: 'Vanuatu', VA: 'Vatican City', VE: 'Venezuela',
  VN: 'Vietnam', WF: 'Wallis and Futuna', EH: 'Western Sahara', YE: 'Yemen',
  ZM: 'Zambia', ZW: 'Zimbabwe',
};

// Country list: popular first, then the rest alphabetically by name
const ORDERED_COUNTRIES = [
  ...POPULAR_COUNTRIES,
  ...Object.keys(COUNTRY_CODES)
    .filter(c => !POPULAR_COUNTRIES.includes(c))
    .sort((a, b) => (COUNTRY_NAMES[a] || a).localeCompare(COUNTRY_NAMES[b] || b)),
];
interface IAMViewProps {
  authUser: AuthUser | null;
  moduleStatuses?: Partial<Record<ModuleId, ModuleStatus>>;
  moduleOutletCounts?: Record<string, number>;
  moduleSeatLimits?: Record<string, number>;
  suiteSeatLimit?: number | null;
}

const PERM_LABELS: { key: string; label: string; group: string }[] = [
  { key: 'canAccessGenerator',  label: 'Access Invoice Generator',     group: 'Navigation' },
  { key: 'canAccessInvoiceCreator', label: 'Access Invoice Creator',   group: 'Navigation' },
  { key: 'canAccessBatchGenerate', label: 'Access Batch Generate',     group: 'Navigation' },
  { key: 'canAccessBills',      label: 'Access Bills / Credit Book',        group: 'Navigation' },
  { key: 'canAccessAccounts',   label: 'Access Client Accounts',       group: 'Accounts' },
  { key: 'canAccessProducts',   label: 'Access Product Catalog',       group: 'Products' },
  { key: 'canAccessLogs',       label: 'Access Audit Logs',            group: 'Navigation' },
  { key: 'canAccessTeam',       label: 'Access Team / IAM',            group: 'Navigation' },
  { key: 'canEditInvoice',      label: 'Edit Invoices',                group: 'Invoices' },
  { key: 'canDeleteInvoice',    label: 'Delete Invoices & Purge Logs', group: 'Invoices' },
  { key: 'canChangeInvoiceStatus', label: 'Change Invoice Status',     group: 'Invoices' },
  { key: 'canEditAccounts',     label: 'Edit Client Accounts',         group: 'Accounts' },
  { key: 'canDeleteAccounts',   label: 'Delete Client Accounts',       group: 'Accounts' },
  { key: 'canSendPromo',        label: 'Send Promo Campaigns',         group: 'Accounts' },
  { key: 'canAddProduct',       label: 'Add Products',                 group: 'Products' },
  { key: 'canEditProduct',      label: 'Edit Products',                group: 'Products' },
  { key: 'canRemoveProduct',    label: 'Remove Products',              group: 'Products' },
  { key: 'canImportProducts',   label: 'Import Products (CSV)',        group: 'Products' },
  { key: 'canAccessPOS',    label: 'Access POS',    group: 'POS' },
  { key: 'canAccessLedger', label: 'Access Credit Book',  group: 'Credit Book' },
  { key: 'canPOSApplyDiscount',  label: 'Apply Discount in POS',     group: 'POS' },
{ key: 'canPOSOverridePrice',  label: 'Override Item Price in POS', group: 'POS' },
{ key: 'canPOSChangeStatus',   label: 'Change Invoice Status in POS', group: 'POS' },
{ key: 'canPOSOverrideInvNo',  label: 'Override Invoice Number',    group: 'POS' },
{ key: 'canPOSChangeBusinessType', label: 'Change Business Type in POS', group: 'POS' },
{ key: 'canPOSEditDateTime',       label: 'Edit Date/Time in POS',       group: 'POS' },
{ key: 'canPOSEditTaxLines',       label: 'Edit Tax Lines in POS',       group: 'POS' },
{ key: 'canPOSEditShipping',       label: 'Edit Shipping in POS',        group: 'POS' },
{ key: 'canPOSManageAutoActions',  label: 'Manage Auto Actions in POS',  group: 'POS' },
{ key: 'canPOSDeleteBill',         label: 'Delete Bill in POS',          group: 'POS' },
{ key: 'canPOSDiscardBill',        label: 'Discard Bill in POS',         group: 'POS' },
{ key: 'canAccessPaymentDisplay',   label: 'Access Customer/Payment Display', group: 'POS' },
{ key: 'canPOSToggleBilingual',     label: 'Toggle Bilingual Receipts',  group: 'POS' },
{ key: 'canAccessTables',           label: 'Access Table Management',    group: 'Table Management' },
{ key: 'canEditTableLayout',        label: 'Design Table Layout',        group: 'Table Management' },
{ key: 'canManageFloors',           label: 'Manage Floors',              group: 'Table Management' },
{ key: 'canAccessKitchenDashboard', label: 'Access Kitchen Dashboard',   group: 'Table Management' },
{ key: 'canLedgerCreateTab',   label: 'Create New Customer Tab',    group: 'Credit Book' },
{ key: 'canLedgerAddCharge',   label: 'Add Charge to Tab',          group: 'Credit Book' },
{ key: 'canLedgerAddPayment',  label: 'Record Payment',             group: 'Credit Book' },
{ key: 'canLedgerSettle',      label: 'Settle Tab',                 group: 'Credit Book' },
{ key: 'canLedgerDeleteEntry', label: 'Delete Ledger Entry',        group: 'Credit Book' },
{ key: 'canLedgerBroadcast',   label: 'Broadcast Messages',         group: 'Credit Book' },
{ key: 'canLedgerSendWA',      label: 'Send WA Reminder',           group: 'Credit Book' },
{ key: 'canLedgerExportExcel', label: 'Export Excel (single tab)',  group: 'Credit Book' },
{ key: 'canLedgerShareQR',     label: 'Share Customer QR',          group: 'Credit Book' },
{ key: 'canLedgerQuickMsg',    label: 'Send Quick Message',         group: 'Credit Book' },
{ key: 'canLedgerNotes',       label: 'Edit Notes & Alert Threshold', group: 'Credit Book' },
{ key: 'canLedgerRecurring',   label: 'Manage Recurring Charges',   group: 'Credit Book' },
{ key: 'canLedgerPayLink',     label: 'Send Payment Link',          group: 'Credit Book' },
{ key: 'canLedgerEditTab',     label: 'Edit Customer Tab',          group: 'Credit Book' },
{ key: 'canLedgerDeleteTab',   label: 'Delete Customer Tab',        group: 'Credit Book' },
{ key: 'canLedgerViewStatus',  label: 'View Ledger Status Dashboard', group: 'Credit Book' },
{ key: 'canLedgerManageCategories', label: 'Manage Charge Categories', group: 'Credit Book' },
{ key: 'canManageOutlets',     label: 'Manage Outlets & Branches',  group: 'Outlets' },
{ key: 'canManageProfileMessaging', label: 'Manage WhatsApp & Gmail in Profile', group: 'Profile' },
{ key: 'canManageCurrency', label: 'Manage Currency & Region', group: 'Profile' },
{ key: 'canManageGateways', label: 'Manage Payment Gateways', group: 'Profile' },
{ key: 'canManageOutletStaff', label: 'Add / Remove Outlet Staff',  group: 'Outlets' },
{ key: 'canAccessInventory',   label: 'Access Inventory',          group: 'Inventory' },
{ key: 'canAccessEmployees',   label: 'Access Employees',          group: 'Employees' },
{ key: 'canManageInventory',   label: 'Manage Inventory & Stock',  group: 'Inventory' },
{ key: 'canManageSuppliers',   label: 'Manage Suppliers & POs',    group: 'Inventory' },
{ key: 'canManageEmployees',   label: 'Add / Edit Employees',      group: 'Employees' },
{ key: 'canViewPayroll',       label: 'View Payroll',              group: 'Employees' },
{ key: 'canManagePayroll',     label: 'Manage Payroll',            group: 'Employees' },
{ key: 'canViewLeaves',        label: 'View Leaves',               group: 'Employees' },
{ key: 'canRequestLeave',      label: 'Request Leave (Self)',      group: 'Employees' },
{ key: 'canManageLeaves',      label: 'Approve / Reject Leaves',   group: 'Employees' },
{ key: 'canViewAttendance',    label: 'View Attendance',           group: 'Employees' },
{ key: 'canManageAttendance',  label: 'Manage Attendance & Kiosk', group: 'Employees' },
{ key: 'canQueueIssueToken',   label: 'Issue Tokens',               group: 'Queue' },
{ key: 'canQueueCallNext',     label: 'Call Next Token',            group: 'Queue' },
{ key: 'canQueueUpdateStatus', label: 'Update Token Status (Call / Hold / Done / Skip)', group: 'Queue' },
{ key: 'canQueueSendWhatsApp', label: 'Send WhatsApp Notifications', group: 'Queue' },
{ key: 'canQueueSendEmail',    label: 'Send Email Notifications',   group: 'Queue' },
{ key: 'canQueueEditToken',    label: 'Edit Token Details',         group: 'Queue' },
{ key: 'canQueueDeleteToken',  label: 'Delete Tokens',              group: 'Queue' },
{ key: 'canQueueManageCounters', label: 'Add / Delete Counters',   group: 'Queue' },
{ key: 'canQueueSettings',     label: 'Edit Queue Settings',        group: 'Queue' },
{ key: 'canQueueViewLogs',     label: 'View Queue Logs',            group: 'Queue' },
{ key: 'canGymCheckInQR',       label: 'Check In via QR',          group: 'Gym' },
{ key: 'canGymCheckInFace',     label: 'Check In via Face',         group: 'Gym' },
{ key: 'canGymCheckInFinger',   label: 'Check In via Fingerprint',  group: 'Gym' },
{ key: 'canGymAddMember',       label: 'Add Member',                group: 'Gym' },
{ key: 'canGymEditMember',      label: 'Edit Member',               group: 'Gym' },
{ key: 'canGymDeleteMember',    label: 'Delete Member',             group: 'Gym' },
{ key: 'canGymViewBadge',       label: 'View Member Badge',         group: 'Gym' },
{ key: 'canGymRegisterFace',    label: 'Register Face',             group: 'Gym' },
{ key: 'canGymVerifyOtp',       label: 'Verify OTP',                group: 'Gym' },
{ key: 'canGymSendReports',     label: 'Send Reports',              group: 'Gym' },
{ key: 'canGymSendPaymentLink', label: 'Send Payment Link',         group: 'Gym' },
{ key: 'canGymViewStatus',      label: 'View Member Status',        group: 'Gym' },
{ key: 'canGymManagePlans',     label: 'Manage Plans',              group: 'Gym' },
{ key: 'canGymManageKiosk',     label: 'Manage Kiosk',              group: 'Gym' },
{ key: 'canFoodCheckInQR',       label: 'Check In via QR',          group: 'Food' },
{ key: 'canFoodCheckInFace',     label: 'Check In via Face',         group: 'Food' },
{ key: 'canFoodCheckInFinger',   label: 'Check In via Fingerprint',  group: 'Food' },
{ key: 'canFoodAddMember',       label: 'Add Member',                group: 'Food' },
{ key: 'canFoodEditMember',      label: 'Edit Member',               group: 'Food' },
{ key: 'canFoodDeleteMember',    label: 'Delete Member',             group: 'Food' },
{ key: 'canFoodViewBadge',       label: 'View Member Badge',         group: 'Food' },
{ key: 'canFoodRegisterFace',    label: 'Register Face',             group: 'Food' },
{ key: 'canFoodVerifyOtp',       label: 'Verify OTP',                group: 'Food' },
{ key: 'canFoodSendReports',     label: 'Send Reports',              group: 'Food' },
{ key: 'canFoodSendPaymentLink', label: 'Send Payment Link',         group: 'Food' },
{ key: 'canFoodViewStatus',      label: 'View Member Status',        group: 'Food' },
{ key: 'canFoodManagePlans',     label: 'Manage Plans',              group: 'Food' },
{ key: 'canFoodManageKiosk',     label: 'Manage Kiosk',              group: 'Food' },
{ key: 'canAccessQueue',   label: 'Access Queue',   group: 'Queue' },
{ key: 'canAccessClinic',  label: 'Access Clinic',  group: 'Queue' },
{ key: 'canAccessFitness', label: 'Access Fitness', group: 'Gym' },
{ key: 'canAccessFood',    label: 'Access Food',    group: 'Food' },
{ key: 'canExportPDF',      label: 'Export PDF',           group: 'Invoices' },
{ key: 'canExportExcel',    label: 'Export Excel',          group: 'Invoices' },
{ key: 'canShareInvoice',   label: 'Share Invoice',         group: 'Invoices' },
{ key: 'canViewAllOutlets', label: 'View All Outlets',      group: 'Outlets' },

];

const ALL_PERMS_TRUE: Record<string, boolean> = Object.fromEntries(PERM_LABELS.map(p => [p.key, true]));
const DEFAULT_PERMS: Record<string, boolean> = Object.fromEntries(PERM_LABELS.map(p => [p.key, false]));

// Maps a module-seat role back to its module id, so we can check lock status.
const ROLE_TO_MODULE_ID: Record<string, string> = {
  ledger_member: 'ledger', queue_member: 'queue', employee_member: 'employees',
  gym_member: 'gym', canteen_member: 'canteen', pos_member: 'pos',
};

// True when this member's role belongs to a module whose plan has expired.
function isMemberModuleLocked(role: string, moduleStatuses: Partial<Record<ModuleId, ModuleStatus>>) {
  const modId = ROLE_TO_MODULE_ID[role];
  if (!modId) return false;
  return moduleStatuses?.[modId as ModuleId] === 'locked';
}
function PlanUsageBar({ label, used, limit, color = '#6366f1' }: { label: string; used: number; limit: number; color?: string }) {
  const pct = limit >= 9999 ? 100 : Math.min(100, Math.round((used / limit) * 100));
  const isUnlimited = limit >= 9999;
  const isNearLimit = pct >= 80;
  const barColor = isNearLimit ? '#ef4444' : color;

  return (
    <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 14px', marginBottom: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: '#475569' }}>{label}</span>
        <span style={{ fontSize: 12, fontWeight: 700, color: isNearLimit ? '#ef4444' : '#1e293b' }}>
          {isUnlimited ? `${used} / Unlimited` : `${used} / ${limit}`}
        </span>
      </div>
      <div style={{ background: '#e2e8f0', borderRadius: 99, height: 6, overflow: 'hidden' }}>
        <div style={{
          width: `${pct}%`, height: '100%',
          background: barColor,
          borderRadius: 99,
          transition: 'width 0.3s ease',
        }} />
      </div>
      {!isUnlimited && (
        <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>
          {limit - used} {label.toLowerCase()} remaining
        </div>
      )}
    </div>
  );
}
// ─── Member Detail Panel ───────────────────────────────────────────────────────
function MemberDetailPanel({
  member,
  isOwner,
  canManage,
  currentUserId,
  authUser,
  onClose,
  onResetPassword,
  onDeactivate,
  onToggleAdmin,
  onPermsSaved,
  moduleStatuses,
  hasLedger, hasQueue, hasEmployees, hasGym, hasCanteen, hasPos,
  outlets,       // NEW
  BASE,          // NEW
  headers,       // NEW
  members,       // NEW
  moduleSeatLimits, // NEW
}: {
  member: TeamMember;
  isOwner: boolean;
  canManage: boolean;
  currentUserId: number | undefined;
  onClose: () => void;
  onResetPassword: (m: TeamMember) => void;
  onDeactivate: (m: TeamMember) => void;
  onToggleAdmin: (m: TeamMember) => void;
  onPermsSaved: (userId: number, perms: Record<string, boolean>) => void;
  moduleStatuses?: Partial<Record<ModuleId, ModuleStatus>>;
  hasLedger: boolean; hasQueue: boolean; hasEmployees: boolean;
hasGym: boolean; hasCanteen: boolean; hasPos: boolean;
  outlets: Outlet[];              // NEW
  BASE: string;                   // NEW
  headers: Record<string, string>; // NEW
  members: TeamMember[];          // NEW — for computing seat usage per module
  moduleSeatLimits: Record<string, number>; // NEW — reflects purchased add-on seats
}) {
  const isOwnerMember = member.role === 'owner';
  const isAdminMember = member.role === 'admin';
  const isLockedOut = isMemberModuleLocked(member.role, moduleStatuses || {});
  const [perms, setPerms] = useState<Record<string, boolean>>(
    isOwnerMember ? ALL_PERMS_TRUE : { ...DEFAULT_PERMS, ...(member.permissions || {}) }
  );
  // Keys the owner has explicitly touched this session (via a single toggle,
  // Grant All, or Revoke All). For module-seat roles we only ever save these
  // keys — everything untouched stays absent from dbPerms so it keeps
  // inheriting from the module gate instead of getting zeroed out.
  const [dirtyKeys, setDirtyKeys] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [loadError, setLoadError] = useState('');
  const isLedgerMember   = member.role === 'ledger_member';
  const isQueueMember    = member.role === 'queue_member';
  const isEmployeeMember = member.role === 'employee_member';
  const isGymMember     = member.role === 'gym_member';
const isCanteenMember = member.role === 'canteen_member';
const isPosMember     = member.role === 'pos_member';
const groups = [...new Set(PERM_LABELS.map(p => p.group))]
  useEffect(() => {
    if (isOwnerMember) return;
    fetchPermissions(member.id)
      .then(p => setPerms({ ...DEFAULT_PERMS, ...p }))
      .catch(() => setLoadError('Failed to load permissions'));
  }, [member.id, member.role]);

  const [memberOutletIds, setMemberOutletIds] = useState<number[]>([]);
  const [outletsLoadingPanel, setOutletsLoadingPanel] = useState(false);

  useEffect(() => {
    if (isOwnerMember) return;
    setOutletsLoadingPanel(true);
    Promise.all(
      outlets.map(o =>
        fetch(`${BASE}/outlets/${o.id}/members`, { headers })
          .then(r => r.json())
          .then((members: any[]) => (members.some(m => m.user_id === member.id) ? o.id : null))
          .catch(() => null)
      )
    ).then(results => {
      setMemberOutletIds(results.filter((id): id is number => id !== null));
      setOutletsLoadingPanel(false);
    });
  }, [member.id, outlets]);

  const toggleMemberOutlet = async (outletId: number) => {
    const isAssigned = memberOutletIds.includes(outletId);
    try {
      if (isAssigned) {
        await fetch(`${BASE}/outlets/${outletId}/members/${member.id}`, { method: 'DELETE', headers });
        setMemberOutletIds(prev => prev.filter(id => id !== outletId));
      } else {
        await fetch(`${BASE}/outlets/${outletId}/members`, {
          method: 'POST', headers,
          body: JSON.stringify({ user_id: member.id, role: member.role }),
        });
        setMemberOutletIds(prev => [...prev, outletId]);
      }
    } catch (e: any) {
      alert('Failed to update outlet access: ' + e.message);
    }
  };

  const toggle = (key: string) => {
    if (!canManage || isOwnerMember || isLockedOut) return;
    setPerms(prev => ({ ...prev, [key]: !prev[key] }));
    setDirtyKeys(prev => new Set(prev).add(key));
    setSaved(false);
  };

  const isModuleSeatRole = ['ledger_member', 'queue_member', 'employee_member', 'gym_member', 'canteen_member', 'pos_member'].includes(member.role);

  const handleSave = async () => {
  try {
    setSaving(true);
    // Module-seat roles: only persist the keys the owner actually touched.
    // Saving the full `perms` object here would include every untouched key
    // at its DEFAULT_PERMS value of false, overriding every permission the
    // member currently has via the module gate — same class of bug as the
    // create/changeRole one, just triggered by a single checkbox + Save.
    const toSave = isModuleSeatRole
      ? Object.fromEntries([...dirtyKeys].map(k => [k, perms[k]]))
      : perms;
    await savePermissions(member.id, toSave);
    onPermsSaved(member.id, toSave);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);

    // ← ADD THIS
    const { postLog } = await import('../utils/authApi');
    await postLog('IAM_PERMISSIONS_CHANGED', `Permissions updated for ${member.name} (${member.email})`);

  } catch (e: any) {
    alert(e.message);
  } finally {
    setSaving(false);
  }
};

  const grantAll = () => { setPerms({ ...ALL_PERMS_TRUE }); setDirtyKeys(new Set(PERM_LABELS.map(p => p.key))); setSaved(false); };
  const revokeAll = () => { setPerms({ ...DEFAULT_PERMS }); setDirtyKeys(new Set(PERM_LABELS.map(p => p.key))); setSaved(false); };
  const grantedCount = isOwnerMember ? PERM_LABELS.length : Object.values(perms).filter(Boolean).length;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 200, display: 'flex', alignItems: 'stretch', justifyContent: 'flex-end' }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.25)' }} />
      <div style={{
        position: 'relative', width: '460px', maxWidth: '95vw',
        background: '#fff', boxShadow: '-4px 0 32px rgba(0,0,0,0.12)',
        display: 'flex', flexDirection: 'column', overflowY: 'auto',
      }}>
        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb', background: '#f8fafc', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
            <span style={{ fontSize: '11px', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              User Permissions
            </span>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', padding: '2px' }}>
              <X size={18} />
            </button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{
              width: '48px', height: '48px', borderRadius: '10px',
              background: isOwnerMember ? 'linear-gradient(135deg, #f59e0b, #ef4444)' : 'linear-gradient(135deg, #3b82f6, #9333ea)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontWeight: 700, fontSize: '18px', flexShrink: 0,
            }}>
              {member.name?.charAt(0).toUpperCase()}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontWeight: 700, fontSize: '16px', color: '#111827' }}>{member.name}</span>
                {isOwnerMember && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '3px', fontSize: '10px', fontWeight: 700, color: '#b45309', background: '#fef3c7', border: '1px solid #fde68a', padding: '2px 7px', borderRadius: '99px' }}>
                    <Crown size={10} /> OWNER
                  </span>
                )}
                {isAdminMember && !isOwnerMember && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '3px', fontSize: '10px', fontWeight: 700, color: '#1d4ed8', background: '#eff6ff', border: '1px solid #bfdbfe', padding: '2px 7px', borderRadius: '99px' }}>
                    <ShieldCheck size={10} /> ADMIN
                  </span>
                )}
              </div>
              <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{member.email}</div>
            </div>
            <div style={{ textAlign: 'center', padding: '6px 12px', borderRadius: '8px', background: '#eff6ff', border: '1px solid #bfdbfe', flexShrink: 0 }}>
              <div style={{ fontSize: '18px', fontWeight: 800, color: '#2563eb', lineHeight: 1 }}>{grantedCount}</div>
              <div style={{ fontSize: '10px', color: '#60a5fa', fontWeight: 600, marginTop: '2px' }}>/ {PERM_LABELS.length}</div>
            </div>
          </div>

          {/* Owner locked notice */}
          {isOwnerMember && (
            <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '6px', background: '#fef3c7', color: '#92400e', padding: '8px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: 600 }}>
              <Crown size={12} /> Owner has full access — permissions cannot be modified
            </div>
          )}

          {!member.is_active && (
            <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '6px', background: '#fee2e2', color: '#dc2626', padding: '6px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: 600 }}>
              <UserX size={12} /> This account is deactivated
            </div>
          )}

          {isLockedOut && (
            <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '6px', background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', padding: '8px 10px', borderRadius: '6px', fontSize: '12px', fontWeight: 600 }}>
              🔒 This member's module plan has expired — they've lost access until it's renewed. Permissions can't be edited while locked.
            </div>
          )}
        </div>

        {/* Role selector (owner only, non-owner members) */}
        {isOwner && !isOwnerMember && member.is_active && (
          <div style={{ padding: '12px 24px', borderBottom: '1px solid #f1f5f9', flexShrink: 0 }}>
            <div style={{ fontSize: '13px', fontWeight: 600, color: '#111827', marginBottom: 8 }}>Role</div>
            {(() => {
              const MODULE_ROLES = ['ledger_member', 'queue_member', 'employee_member', 'gym_member', 'canteen_member', 'pos_member'];
              const isModuleRole = MODULE_ROLES.includes(member.role);
              const anyModuleActive = hasLedger || hasQueue || hasEmployees || hasGym || hasCanteen || hasPos;

              const changeRole = async (newRole: string) => {
                try {
                  await updateRole(member.id, newRole);
                  // Module-seat roles get their real permissions from the module
                  // gate in getPermissions(), which runs BEFORE dbPerms is applied.
                  // Saving an all-false snapshot here would override every
                  // module-granted permission back to false — so skip it and
                  // leave dbPerms empty for these roles instead.
                  const isModuleSeatRole = ['ledger_member', 'queue_member', 'employee_member', 'gym_member', 'canteen_member', 'pos_member'].includes(newRole);
                  const defaults = isModuleSeatRole ? {} : { ...DEFAULT_PERMS, ...(ROLE_PERMISSIONS[newRole as Role] || {}) };
                  setPerms({ ...DEFAULT_PERMS, ...defaults });
                  await savePermissions(member.id, defaults);
                  onPermsSaved(member.id, defaults);
                  onToggleAdmin({ ...member, role: newRole });

                  // Same auto-detect as the "Add Member" modal — if this role
                  // maps to a module and exactly one outlet has that module
                  // enabled, assign it exclusively (drop any outlets that
                  // don't belong to this module, add the dedicated one).
                  const modId = ROLE_TO_MODULE_ID[newRole];
                  if (modId) {
                    const matching = outlets.filter(o => o.modules?.includes(modId));
                    if (matching.length === 1) {
                      const targetId = matching[0].id;
                      for (const outletId of memberOutletIds) {
                        if (outletId !== targetId) await toggleMemberOutlet(outletId);
                      }
                      if (!memberOutletIds.includes(targetId)) await toggleMemberOutlet(targetId);
                    }
                  }

                  setSaved(true);
                  setTimeout(() => setSaved(false), 2000);
                } catch (e: any) { alert((e as any).message); }
              };

              return (
                <>
                  {/* Toggle: Staff Role vs Module Access */}
                  <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                    <button
                      type="button"
                      onClick={() => { if (isModuleRole) changeRole('staff'); }}
                      style={{
                        flex: 1, padding: '6px', borderRadius: 8, cursor: 'pointer',
                        border: `1.5px solid ${!isModuleRole ? '#3b82f6' : '#e5e7eb'}`,
                        background: !isModuleRole ? '#eff6ff' : '#fff',
                        color: !isModuleRole ? '#1d4ed8' : '#6b7280',
                        fontSize: 12, fontWeight: 700,
                      }}
                    >
                      👤 Staff Role
                    </button>
                    {anyModuleActive && (
                      <button
                        type="button"
                        onClick={() => {
                          if (!isModuleRole) {
                            const firstModuleRole =
  hasLedger ? 'ledger_member' :
  hasQueue ? 'queue_member' :
  hasEmployees ? 'employee_member' :
  hasGym ? 'gym_member' :
  hasCanteen ? 'canteen_member' : 'pos_member';
                              
                            changeRole(firstModuleRole);
                          }
                        }}
                        style={{
                          flex: 1, padding: '6px', borderRadius: 8, cursor: 'pointer',
                          border: `1.5px solid ${isModuleRole ? '#3b82f6' : '#e5e7eb'}`,
                          background: isModuleRole ? '#eff6ff' : '#fff',
                          color: isModuleRole ? '#1d4ed8' : '#6b7280',
                          fontSize: 12, fontWeight: 700,
                        }}
                      >
                        🧩 Module Access
                      </button>
                    )}
                  </div>

                  {!isModuleRole && (
                    <select
                      value={member.role}
                      onChange={e => changeRole(e.target.value)}
                      style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 13, color: '#111827', background: '#f8fafc', cursor: 'pointer', outline: 'none' }}
                    >
                      <option value="cashier">Cashier </option>
                      <option value="staff">Staff </option>
                      <option value="manager">Manager </option>
                      <option value="admin">Admin </option>
                      <option value="viewer">Viewer </option>
                    </select>
                  )}

                  {isModuleRole && (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, border: '1px solid #e5e7eb', borderRadius: 6, padding: '8px 10px' }}>
    {[
      { role: 'ledger_member', modId: 'ledger', label: '📒 Credit Book Member', active: hasLedger },
      { role: 'queue_member', modId: 'queue', label: '🎫 Queue Member', active: hasQueue },
      { role: 'employee_member', modId: 'employees', label: '👥 Attendance Member', active: hasEmployees },
      { role: 'gym_member', modId: 'gym', label: '🏋️ Fitness Center Member', active: hasGym },
      { role: 'canteen_member', modId: 'canteen', label: '🍽️ Meal Plans & Dining Member', active: hasCanteen },
      { role: 'pos_member', modId: 'pos', label: '🧾 POS Member', active: hasPos },
      ].filter(m => m.active).map(m => {
      const isTrial = moduleStatuses[m.modId as ModuleId] === 'trial';
      const cap = isTrial ? Infinity : (moduleSeatLimits[m.modId] ?? 2);
      const used = members.filter(mm => mm.is_active && mm.role === m.role && mm.id !== member.id).length;
      const isSelected = member.role === m.role;
      const isFull = !isSelected && used >= cap;
      return (
        <label key={m.role} style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
          fontSize: 13, color: isFull ? '#9ca3af' : '#374151',
          cursor: isFull ? 'not-allowed' : 'pointer', opacity: isFull ? 0.6 : 1,
        }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="radio"
              name="module-role-edit"
              checked={isSelected}
              disabled={isFull}
              onChange={() => { if (!isFull) changeRole(m.role); }}
            />
            {m.label}
          </span>
          <span style={{ fontSize: 10, fontWeight: 700, color: isFull ? '#dc2626' : '#94a3b8' }}>
            {used}/{cap === Infinity ? '∞' : cap}{isFull ? ' · full' : ''}
          </span>
        </label>
      );
    })}
  </div>
)}
                </>
              );
            })()}
          </div>
        )}

        {/* Quick actions */}
        {canManage && !isOwnerMember && member.is_active && (
          <div style={{ padding: '12px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', gap: '8px', flexShrink: 0 }}>
            <button onClick={grantAll} style={{ flex: 1, padding: '6px', borderRadius: '6px', border: '1px solid #bbf7d0', background: '#f0fdf4', color: '#166534', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
              Grant All
            </button>
            <button onClick={revokeAll} style={{ flex: 1, padding: '6px', borderRadius: '6px', border: '1px solid #fecaca', background: '#fff5f5', color: '#dc2626', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}>
              Revoke All
            </button>
          </div>
        )}

        {/* Outlet access */}
        {canManage && !isOwnerMember && member.is_active && outlets.length > 0 && (() => {
          // Module-role members are strictly confined to outlets that carry
          // that module — no manually checking an unrelated outlet.
          const memberModId = ROLE_TO_MODULE_ID[member.role];
          return (
          <div style={{ padding: '12px 24px', borderBottom: '1px solid #f1f5f9' }}>
            <div style={{ fontSize: '13px', fontWeight: 600, color: '#111827', marginBottom: 8 }}>Outlet Access</div>
            {outletsLoadingPanel ? (
              <div style={{ fontSize: 12, color: '#9ca3af' }}>Loading...</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, border: '1px solid #e5e7eb', borderRadius: 6, padding: '8px 10px', maxHeight: 140, overflowY: 'auto' }}>
                {outlets.map(o => {
                  const isAllowed = !memberModId || o.modules?.includes(memberModId);
                  return (
                    <label key={o.id} style={{
                      display: 'flex', alignItems: 'center', gap: 8, fontSize: 13,
                      color: isAllowed ? '#374151' : '#c1c7d0',
                      cursor: isAllowed ? 'pointer' : 'not-allowed',
                    }}>
                      <input
                        type="checkbox"
                        checked={memberOutletIds.includes(o.id)}
                        disabled={!isAllowed}
                        onChange={() => { if (isAllowed) toggleMemberOutlet(o.id); }}
                      />
                      {o.name}
                      {!isAllowed && <span style={{ fontSize: 10 }}>· not on this module</span>}
                    </label>
                  );
                })}
              </div>
            )}
            {!outletsLoadingPanel && memberOutletIds.length === 0 && (
              <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
                No outlets assigned — this member has no outlet access.
              </div>
            )}
          </div>
          );
        })()}

        {loadError && (
          <div style={{ margin: '12px 24px', display: 'flex', alignItems: 'center', gap: '6px', color: '#dc2626', fontSize: '13px' }}>
            <AlertCircle size={14} /> {loadError}
          </div>
        )}

        {/* Permission toggles */}
        <div style={{ padding: '16px 24px', flex: 1 }}>
          {groups.map(group => (
            <div key={group} style={{ marginBottom: '20px' }}>
              <div style={{ fontSize: '10px', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '8px', paddingBottom: '6px', borderBottom: '1px solid #f1f5f9' }}>
                {group}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                {PERM_LABELS.filter(p => p.group === group).map(perm => {
                  const granted = isOwnerMember ? true : (perms[perm.key] ?? false);
                  const locked = isOwnerMember;
                  return (
                    <div
                      key={perm.key}
                      onClick={() => !locked && toggle(perm.key)}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '9px 12px', borderRadius: '6px',
                        cursor: locked ? 'not-allowed' : canManage ? 'pointer' : 'default',
                        background: granted ? '#eff6ff' : 'transparent',
                        border: `1px solid ${granted ? '#bfdbfe' : 'transparent'}`,
                        opacity: locked ? 0.75 : 1,
                        transition: 'all 0.12s',
                      }}
                      onMouseEnter={e => { if (canManage && !locked) (e.currentTarget as HTMLElement).style.background = granted ? '#dbeafe' : '#f8fafc'; }}
                      onMouseLeave={e => { if (!locked) (e.currentTarget as HTMLElement).style.background = granted ? '#eff6ff' : 'transparent'; }}
                    >
                      <span style={{ fontSize: '13px', color: granted ? '#1d4ed8' : '#6b7280', fontWeight: granted ? 500 : 400 }}>
                        {perm.label}
                      </span>
                      <div style={{
                        width: '36px', height: '20px', borderRadius: '10px', flexShrink: 0,
                        background: granted ? (locked ? '#93c5fd' : '#3b82f6') : '#d1d5db',
                        position: 'relative', transition: 'background 0.2s',
                      }}>
                        <div style={{
                          position: 'absolute', top: '2px',
                          left: granted ? '18px' : '2px',
                          width: '16px', height: '16px', borderRadius: '50%',
                          background: '#fff', transition: 'left 0.2s',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                        }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Cashier Badge QR */}
        {member.is_active && (
          <div style={{ padding: '16px 24px', borderTop: '1px solid #f1f5f9' }}>
            <div style={{ fontSize: '10px', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '12px' }}>
              Cashier Login Badge
            </div>
            <MemberBadgeQR member={member} />
          </div>
        )}

        {/* Save + actions */}
        
        {(canManage || member.id === currentUserId) && !isOwnerMember && member.is_active && (
  <div style={{ padding: '16px 24px', borderTop: '1px solid #e5e7eb', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
    <button
      onClick={handleSave}
              disabled={saving || isLockedOut}
              style={{
                width: '100%', padding: '10px', borderRadius: '8px', border: 'none',
                background: saved ? '#10b981' : 'linear-gradient(to right, #3b82f6, #9333ea)',
                color: '#fff', fontWeight: 600, fontSize: '14px', cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.7 : 1, transition: 'background 0.2s',
              }}
            >
              {saving ? 'Saving...' : saved ? '✓ Permissions Saved' : 'Save Permissions'}
            </button>
           <div style={{ display: 'flex', gap: '8px' }}>
  {(canManage || member.id === currentUserId) && (
    <button
      onClick={() => onResetPassword(member)}
      style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', padding: '8px', borderRadius: '6px', border: '1px solid #e5e7eb', background: '#fff', color: '#374151', fontSize: '12px', fontWeight: 500, cursor: 'pointer' }}
    >
      <Key size={13} /> Reset Password
    </button>
  )}
  {canManage && member.role !== 'owner' && (
    <button
      onClick={() => onDeactivate(member)}
      style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', padding: '8px', borderRadius: '6px', border: '1px solid #fecaca', background: '#fff', color: '#dc2626', fontSize: '12px', fontWeight: 500, cursor: 'pointer' }}
    >
      <UserX size={13} /> Delete
    </button>
  )}
</div>
          </div>
        )}
      </div>
</div>
  );
}
function MemberBadgeQR({ member }: { member: TeamMember }) {
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [badgeToken, setBadgeToken] = useState<string | null>(null);
  const [companyName, setCompanyName] = useState('');
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const BASE = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';
  const adminToken = localStorage.getItem('cloudairambo_jwt') || '';

  useEffect(() => {
    if (!adminToken) return;
    fetch(`${BASE}/auth/company-profile`, {
      headers: { Authorization: `Bearer ${adminToken}` },
    })
      .then(r => r.json())
      .then(data => {
        if (data?.name) setCompanyName(data.name);
      })
      .catch(() => {});
  }, [adminToken]);

  const buildQr = (token: string) => {
    const qrValue = `CASHIER_TOKEN:${token}`;
    import('qrcode').then(QRCode => {
      QRCode.toDataURL(qrValue, { width: 400, margin: 3 })
        .then(url => setQrDataUrl(url))
        .catch(() => {});
    });
  };

  const loadStatus = () => {
    setLoading(true);
    setError('');
    setQrDataUrl('');
    fetch(`${BASE}/iam/users/${member.id}/badge-token`, {
      headers: { Authorization: `Bearer ${adminToken}` },
    })
      .then(r => r.json())
      .then(data => {
        setBadgeToken(data.badge_token || null);
        if (data.badge_token) buildQr(data.badge_token);
      })
      .catch(() => setError('Failed to load badge status.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadStatus(); }, [member.id, member.role]);

  const handleGenerate = async () => {
    setGenerating(true);
    setError('');
    try {
      const res = await fetch(`${BASE}/auth/generate-badge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({ user_id: member.id }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to generate badge');
      setBadgeToken(data.badge_token);
      buildQr(data.badge_token);
    } catch (e: any) {
      setError(e.message || 'Failed to generate badge.');
    } finally {
      setGenerating(false);
    }
  };

  const handlePrint = () => {
    if (!qrDataUrl) return;
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`
      <html><head><title>Cashier Badge — ${member.name} | ${companyName || 'Our Company'}</title>
      <style>
        body{font-family:system-ui;text-align:center;padding:40px;margin:0;}
        .badge{display:inline-block;border:3px solid #3b82f6;border-radius:16px;padding:24px 32px;}
        h2{margin:0 0 4px;color:#1e40af;font-size:20px;}
        p{margin:0 0 16px;color:#6b7280;font-size:13px;}
        img{display:block;margin:0 auto;width:160px;height:160px;}
        .role{margin-top:12px;background:#eff6ff;color:#1d4ed8;padding:4px 14px;border-radius:20px;font-size:12px;font-weight:700;display:inline-block;}
      </style></head><body>
      <div class="badge">
        <h2>${member.name}</h2>
        <p>${member.email}</p>
        <img src="${qrDataUrl}" />
        <div class="role">${(member.role || 'staff').toUpperCase()}</div>
      </div>
      <script>window.onload = () => window.print();<\/script>
      </body></html>
    `);
    win.document.close();
  };

  const handleWhatsApp = async () => {
    if (!qrDataUrl) return;
    const phone = prompt('WhatsApp number (e.g. 919876543210):');
    if (!phone) return;
    const activeBrand = companyName || 'Our Company';
    try {
      const res = await fetch(`${BASE}/whatsapp/send-image`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({
          phone,
          image: qrDataUrl,
          caption: `🪪 Cashier Badge — ${member.name}\nRole: ${(member.role || 'staff').toUpperCase()}\n\nScan QR at POS terminal to login. Keep private.\n\n— ${activeBrand}`,
        }),
      });
      const data = await res.json();
      if (data.success) alert('✅ Badge sent!');
      else alert('Failed: ' + (data.error || 'unknown'));
    } catch (e: any) { alert('Error: ' + e.message); }
  };

  if (loading) {
    return <div style={{ textAlign: 'center', fontSize: 11, color: '#9ca3af', padding: 12 }}>Checking badge status...</div>;
  }

  if (!badgeToken) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
        <div style={{ width: 120, height: 120, borderRadius: 10, border: '1px dashed #e5e7eb', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#9ca3af', textAlign: 'center', padding: 8 }}>
          No badge issued yet
        </div>
        {error && <div style={{ fontSize: 11, color: '#dc2626' }}>{error}</div>}
        <button onClick={handleGenerate} disabled={generating}
          style={{ width: '100%', padding: '8px', borderRadius: 7, border: 'none', background: '#3b82f6', color: '#fff', fontSize: 12, fontWeight: 700, cursor: generating ? 'not-allowed' : 'pointer', opacity: generating ? 0.6 : 1 }}>
          {generating ? 'Generating...' : '🪪 Generate Badge'}
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
      {qrDataUrl
        ? <img src={qrDataUrl} alt="Badge QR" style={{ width: 200, height: 200, borderRadius: 10, border: '1px solid #e5e7eb' }} />
        : <div style={{ width: 120, height: 120, borderRadius: 10, border: '1px solid #e5e7eb', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: '#9ca3af' }}>Rendering...</div>
      }
      <div style={{ fontSize: 11, color: '#6b7280', textAlign: 'center' }}>{member.name} · {(member.role || '').toUpperCase()}</div>
      {error && <div style={{ fontSize: 11, color: '#dc2626' }}>{error}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, width: '100%' }}>
        <button onClick={handlePrint} disabled={!qrDataUrl}
          style={{ padding: '7px', borderRadius: 7, border: 'none', background: '#3b82f6', color: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer', opacity: qrDataUrl ? 1 : 0.5 }}>
          🖨️ Print
        </button>
        <button onClick={handleWhatsApp} disabled={!qrDataUrl}
          style={{ padding: '7px', borderRadius: 7, border: 'none', background: '#16a34a', color: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer', opacity: qrDataUrl ? 1 : 0.5 }}>
          📱 WhatsApp
        </button>
      </div>
      <button onClick={handleGenerate} disabled={generating}
        style={{ width: '100%', padding: '7px', borderRadius: 7, border: '1px solid #fecaca', background: '#fff5f5', color: '#dc2626', fontSize: 11, fontWeight: 700, cursor: generating ? 'not-allowed' : 'pointer', opacity: generating ? 0.6 : 1 }}>
        {generating ? 'Regenerating...' : '🔄 Regenerate Badge (invalidates old one)'}
      </button>
      <div style={{ fontSize: 9, color: '#f59e0b', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 6, padding: '4px 8px', textAlign: 'center', width: '100%', boxSizing: 'border-box' }}>
        ⚠ Keep private — scanning logs this person in
      </div>
    </div>
  );
}

function detectCountryCode(): string {
  const userDefault = localStorage.getItem('default_phone_country');
  if (userDefault && COUNTRY_CODES[userDefault]) return userDefault;

  const cached = sessionStorage.getItem('detected_country');
  if (cached && COUNTRY_CODES[cached]) return cached;
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const tzToCountry: Record<string, string> = {
    'Asia/Kolkata':'IN','Asia/Calcutta':'IN','Asia/Dubai':'AE','Asia/Muscat':'OM',
    'Asia/Riyadh':'SA','Asia/Kuwait':'KW','Asia/Qatar':'QA','Asia/Bahrain':'BH',
    'Asia/Karachi':'PK','Asia/Dhaka':'BD','Asia/Kathmandu':'NP','Asia/Colombo':'LK',
    'Asia/Singapore':'SG','Asia/Kuala_Lumpur':'MY','Asia/Jakarta':'ID','Asia/Manila':'PH',
    'Asia/Bangkok':'TH','Asia/Ho_Chi_Minh':'VN','Asia/Tokyo':'JP','Asia/Seoul':'KR',
    'Asia/Shanghai':'CN','Asia/Hong_Kong':'HK','Asia/Taipei':'TW',
    'Europe/London':'GB','Europe/Dublin':'IE','Europe/Paris':'FR','Europe/Berlin':'DE',
    'Europe/Amsterdam':'NL','Europe/Madrid':'ES','Europe/Rome':'IT','Europe/Zurich':'CH',
    'America/New_York':'US','America/Chicago':'US','America/Los_Angeles':'US',
    'America/Toronto':'CA','America/Sao_Paulo':'BR','America/Mexico_City':'MX',
    'Australia/Sydney':'AU','Australia/Melbourne':'AU','Pacific/Auckland':'NZ',
    'Africa/Cairo':'EG','Africa/Lagos':'NG','Africa/Nairobi':'KE','Africa/Johannesburg':'ZA',
  };
  const code = tzToCountry[tz] || 'IN';
  sessionStorage.setItem('detected_country', code);
  return code;
}

function parseOutletPhoneValue(value: string, defaultCountry = 'IN'): { country: string; dialCode: string; nationalNumber: string } {
  const fallbackCountry = defaultCountry && COUNTRY_CODES[defaultCountry] ? defaultCountry : 'IN';
  const fallbackDialCode = (COUNTRY_CODES[fallbackCountry] || '91').replace(/\D/g, '');

  if (!value) return { country: fallbackCountry, dialCode: fallbackDialCode, nationalNumber: '' };

  const raw = String(value).trim();
  const hasLeadingPlus = raw.startsWith('+');
  let cleanDigits = raw.replace(/\D/g, '');

  if (hasLeadingPlus) {
    let bestCountry = fallbackCountry;
    let bestDialCode = fallbackDialCode;
    let maxCodeLen = 0;

    const sortedEntries = Object.entries(COUNTRY_CODES).sort(
      (a, b) => b[1].replace(/\D/g, '').length - a[1].replace(/\D/g, '').length
    );

    for (const [c, code] of sortedEntries) {
      const pureCode = code.replace(/\D/g, '');
      if (cleanDigits.startsWith(pureCode) && pureCode.length > maxCodeLen) {
        bestCountry = c;
        bestDialCode = pureCode;
        maxCodeLen = pureCode.length;
      }
    }

    return {
      country: bestCountry,
      dialCode: bestDialCode,
      nationalNumber: cleanDigits.slice(bestDialCode.length),
    };
  }

  if (cleanDigits.startsWith('0') && fallbackCountry !== 'IT' && cleanDigits.length >= 7) {
    cleanDigits = cleanDigits.replace(/^0/, '');
  }

  if (cleanDigits.startsWith(fallbackDialCode) && cleanDigits.length >= fallbackDialCode.length + 5) {
    return {
      country: fallbackCountry,
      dialCode: fallbackDialCode,
      nationalNumber: cleanDigits.slice(fallbackDialCode.length),
    };
  }

  return {
    country: fallbackCountry,
    dialCode: fallbackDialCode,
    nationalNumber: cleanDigits,
  };
}

export function getCleanE164Phone(phone: string, defaultCountry = 'IN'): string {
  if (!phone) return '';
  const parsed = parseOutletPhoneValue(phone, defaultCountry);
  const national = parsed.nationalNumber.replace(/\D/g, '');
  const dial = parsed.dialCode.replace(/\D/g, '');
  const total = `${dial}${national}`;

  // Must have at least 4 digits locally, and 7 to 15 total international digits
  if (national.length < 4 || total.length < 7 || total.length > 15) {
    return '';
  }
  return `+${dial}${national}`;
}

function IAMCountryPicker({ value, onChange }: { value: string; onChange: (c: string) => void }) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const ref = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const allCodes = Object.keys(COUNTRY_CODES);
  const filtered = search
    ? allCodes.filter(c => c.toLowerCase().includes(search.toLowerCase()) || COUNTRY_CODES[c]?.includes(search.replace('+', '')))
    : [...POPULAR_COUNTRIES, ...allCodes.filter(c => !POPULAR_COUNTRIES.includes(c))];

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button onClick={() => setOpen(!open)} type="button" style={{
        display: 'flex', alignItems: 'center', gap: 3, background: '#fff',
        border: '1px solid #fde68a', borderRight: 'none', borderRadius: '6px 0 0 6px',
        padding: '6px 8px', cursor: 'pointer', fontSize: 11, fontWeight: 700,
        color: '#111827', whiteSpace: 'nowrap' as const, minWidth: 60,
      }}>
        +{COUNTRY_CODES[value] || '91'} <span style={{ fontSize: 8, opacity: 0.5 }}>▾</span>
      </button>
      {open && (
        <div onClick={e => e.stopPropagation()} style={{
          position: 'absolute', top: '100%', left: 0, zIndex: 9999,
          background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
          boxShadow: '0 12px 40px rgba(0,0,0,0.12)', width: 220, maxHeight: 260,
          overflow: 'hidden', display: 'flex', flexDirection: 'column',
        }}>
          <div style={{ padding: 8 }}>
            <input autoFocus placeholder="Search country or +code..." value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ width: '100%', border: '1px solid #e2e8f0', borderRadius: 6, padding: '5px 8px', fontSize: 12, outline: 'none', boxSizing: 'border-box' }} />
          </div>
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {filtered.map(c => (
              <button key={c} type="button" onClick={() => { onChange(c); setOpen(false); setSearch(''); }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                  background: c === value ? '#f0f9ff' : 'transparent',
                  border: 'none', padding: '7px 12px', cursor: 'pointer', fontSize: 12, color: '#374151', textAlign: 'left' as const,
                }}>
                <span style={{ fontWeight: 700, color: '#6366f1', minWidth: 40 }}>+{COUNTRY_CODES[c]}</span>
                <span>{c}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function OutletPhoneInput({ value, onChange, defaultCountry }: { value: string; onChange: (v: string) => void; defaultCountry?: string }) {
  const parsed = parseOutletPhoneValue(value, defaultCountry);
  const [country, setCountry] = React.useState(() => {
    if (value && value.startsWith('+')) return parsed.country;
    if (defaultCountry && COUNTRY_CODES[defaultCountry]) return defaultCountry;
    return detectCountryCode();
  });
  const [number, setNumber] = React.useState(() => parsed.nationalNumber);
  const lastEmitted = React.useRef(value);

  React.useEffect(() => {
    if (value === lastEmitted.current) return;
    lastEmitted.current = value;
    if (!value) {
      setNumber('');
    } else {
      const p = parseOutletPhoneValue(value, defaultCountry);
      setCountry(p.country);
      setNumber(p.nationalNumber);
    }
  }, [value, defaultCountry]);

  const handleCountryChange = (newCountry: string) => {
    setCountry(newCountry);
    const dialCode = COUNTRY_CODES[newCountry] || '91';
    const cleanDigits = number.trim();
    const formatted = cleanDigits ? `+${dialCode}${cleanDigits}` : '';
    lastEmitted.current = formatted;
    onChange(formatted);
  };

  const handleNumberChange = (raw: string) => {
    let cleanNum = raw.replace(/\D/g, '');
    const dialCode = (COUNTRY_CODES[country] || '91').replace(/\D/g, '');

    // Strip dial code if pasted with '+' or duplicate country prefix
    if (raw.includes('+') && cleanNum.startsWith(dialCode)) {
      cleanNum = cleanNum.slice(dialCode.length);
    } else if (cleanNum.startsWith(dialCode) && cleanNum.length > 10) {
      cleanNum = cleanNum.slice(dialCode.length);
    }

    setNumber(cleanNum);
    const formatted = cleanNum ? `+${dialCode}${cleanNum}` : '';
    lastEmitted.current = formatted;
    onChange(formatted);
  };

  return (
    <div style={{ display: 'flex', flex: 1 }}>
      <IAMCountryPicker value={country} onChange={handleCountryChange} />
      <input
        type="tel"
        value={number}
        onChange={e => handleNumberChange(e.target.value)}
        placeholder="Phone number"
        style={{
          flex: 1, border: '1px solid #fde68a', borderLeft: 'none',
          borderRadius: '0 6px 6px 0', padding: '6px 10px',
          fontSize: 12, outline: 'none', background: '#fff',
          fontWeight: 600, color: '#111827',
        }}
      />
    </div>
  );
}
const MODULE_ICONS: Record<string, { icon: string; label: string }> = {
  ledger: { icon: '📒', label: 'Credit Book' },
  queue: { icon: '🎫', label: 'Queue' },
  employees: { icon: '👥', label: 'Attendance' },
  gym: { icon: '🏋️', label: 'Fitness Center' },
  canteen: { icon: '🍽️', label: 'Meal Plans & Dining' },
  pos: { icon: '🧾', label: 'POS' },
};

// ─── Outlet types ──────────────────────────────────────────────────────────────
interface Outlet {
  id: number;
  name: string;
  city: string | null;
  country: string;
  region?: string | null;
  address: string | null;
  currency: string;
  invoice_prefix: string | null;
  whatsapp_port: number | null;
  whatsapp_number: string | null;
  whatsapp_connected: boolean;
  is_active: boolean;
  modules?: string[];
  locked?: boolean;
  suite_tier?: string | null;
  tax_id?: string | null;
}

interface OutletMember {
  id: number;
  user_id: number;
  name: string;
  email: string;
  role: string;
  is_active: boolean;
}

// ─── Main IAMView ──────────────────────────────────────────────────────────────
export default function IAMView({ authUser, moduleStatuses = {}, moduleOutletCounts = {}, moduleSeatLimits = {}, suiteSeatLimit = null }: IAMViewProps) {
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      *::-webkit-scrollbar { width: 0px; height: 0px; display: none; }
      * { scrollbar-width: none; -ms-overflow-style: none; }
    `;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);
  const [activeTab, setActiveTab] = useState<'team' | 'outlets'>(authUser?.role === 'manager' ? 'outlets' : 'team');

  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);

  // ── Outlets state ────────────────────────────────────────────────────────
  const [outlets, setOutlets] = useState<Outlet[]>([]);
  const [outletsLoading, setOutletsLoading] = useState(false);
  const [selectedOutlet, setSelectedOutlet] = useState<Outlet | null>(null);
  const [outletMembers, setOutletMembers] = useState<OutletMember[]>([]);
  const [outletMembersLoading, setOutletMembersLoading] = useState(false);
  const [showCreateOutlet, setShowCreateOutlet] = useState(false);
  const [outletForm, setOutletForm] = useState({ name: '', city: '', country: '', region: '', currency: '', invoice_prefix: '', modules: [] as string[], tax_id: '' });
  const [editingPort, setEditingPort] = useState<{ outletId: number; port: string; mode?: string; number?: string } | null>(null);
  const [showEditOutlet, setShowEditOutlet] = useState(false);
  const [editOutletId, setEditOutletId] = useState<number | null>(null);
  const [editOutletForm, setEditOutletForm] = useState({ name: '', city: '', country: '', region: '', currency: '', tax_id: '' });
  const [savingEditOutlet, setSavingEditOutlet] = useState(false);
  const [savingPort, setSavingPort] = useState(false);
  const [creatingOutlet, setCreatingOutlet] = useState(false);
  const [addMemberOutletUserId, setAddMemberOutletUserId] = useState('');
  const [addMemberOutletRole, setAddMemberOutletRole] = useState('cashier');
  const [showCreateOutletMember, setShowCreateOutletMember] = useState(false);
  const [outletMemberForm, setOutletMemberForm] = useState({ name: '', email_prefix: '', password: '', role: 'cashier' });
  const [creatingOutletMember, setCreatingOutletMember] = useState(false);
  const [outletMemberError, setOutletMemberError] = useState('');

  const BASE = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';
  const jwt = localStorage.getItem('cloudairambo_jwt') || '';
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` };

  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  const [resetTarget, setResetTarget] = useState<TeamMember | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [resetting, setResetting] = useState(false);

const isOwner = authUser?.role === 'owner';
const isAdmin = authUser?.role === 'admin';
const isManager = authUser?.role === 'manager';
const perms = getPermissions(authUser?.role, (authUser as any)?.permissions, (authUser as any)?.plan, (authUser as any)?.plan_tier, (authUser as any)?.plan_status, moduleStatuses);
// Which modules are accessible (trial or active)
const hasLedger    = moduleStatuses?.ledger    === 'trial' || moduleStatuses?.ledger    === 'active';
const hasQueue     = moduleStatuses?.queue     === 'trial' || moduleStatuses?.queue     === 'active';
const hasEmployees = moduleStatuses?.employees === 'trial' || moduleStatuses?.employees === 'active';
const hasGym       = moduleStatuses?.gym       === 'trial' || moduleStatuses?.gym       === 'active';
const hasCanteen   = moduleStatuses?.canteen   === 'trial' || moduleStatuses?.canteen   === 'active';
const hasPos       = moduleStatuses?.pos       === 'trial' || moduleStatuses?.pos       === 'active';
const hasAnyModule = hasLedger || hasQueue || hasEmployees || hasGym || hasCanteen || hasPos;
const MODULE_MEMBER_ROLE_BY_MODULE: Record<string, string> = {
  ledger: 'ledger_member', queue: 'queue_member', employee: 'employee_member',
  employees: 'employee_member', gym: 'gym_member', canteen: 'canteen_member',pos: 'pos_member',
};
// Real outlet limit from paid module outlet_count (not a hardcoded "unlimited")
const anyModuleInTrialTop = Object.values(moduleStatuses || {}).some(s => s === 'trial');
const activeModuleOutletCounts = Object.entries(moduleStatuses || {})
  .filter(([, s]) => s === 'active' || s === 'trial')
  .map(([id]) => moduleOutletCounts[id] ?? 1);
const moduleOutletLimit = anyModuleInTrialTop
  ? Infinity // trial = unlimited outlets while it lasts
  : activeModuleOutletCounts.length > 0
  ? activeModuleOutletCounts.reduce((sum, n) => sum + n, 0)
  : null; // null = no active module, fall back to Suite tier limits

const getOutletLimit = () => {
  return moduleOutletLimit !== null ? moduleOutletLimit : 1;
};
const canManage = isOwner || isAdmin;
const canManageStaff = canManage || (isManager && !!(authUser as any)?.outlet_id);
  const [form, setForm] = useState({ 
    name: '', 
    email_prefix: '', 
    password: '',
    role: authUser?.role === 'owner' ? 'staff' : 'cashier',
  });
  const [selectedOutletIds, setSelectedOutletIds] = useState<number[]>([]);

  // Whenever the assigned role changes (e.g. switching between module roles,
  // or from Staff to Module Access), re-derive which outlets are even valid
  // choices, and auto-pick the outlet if there's exactly one match.
  useEffect(() => {
    if (!showCreate) return;
    const modId = ROLE_TO_MODULE_ID[form.role];
    if (!modId) return; // Staff role — outlet access isn't module-restricted
    const matching = outlets.filter(o => o.modules?.includes(modId));
    setSelectedOutletIds(matching.length === 1 ? [matching[0].id] : []);
  }, [form.role, showCreate, outlets]);

  useEffect(() => { load(); loadOutlets(); }, []);

  async function load() {
    try {
      setLoading(true);
      setError('');
      const data = await fetchTeamMembers();
      setMembers(data);
    } catch (e: any) {
      setError(e.message || 'Failed to load team');
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate() {
    if (!form.name.trim() || !form.email_prefix.trim() || !form.password) {
      setCreateError('All fields are required');
      return;
    }
    try {
      setCreating(true);
      setCreateError('');
      const role = isOwner ? form.role
  : hasLedger    ? 'ledger_member'
  : hasQueue     ? 'queue_member'
  : hasEmployees ? 'employee_member'
  : hasGym       ? 'gym_member'
  : hasCanteen   ? 'canteen_member'
  : 'cashier';

      // Frontend guard mirroring the backend's own per-module seat check —
      // scoped to the role actually being created, not a guessed one.
      const moduleIdForRole = (Object.entries(MODULE_MEMBER_ROLE_BY_MODULE) as [string, string][])
        .find(([, r]) => r === role)?.[0];
      if (moduleIdForRole && moduleStatuses[moduleIdForRole as ModuleId] !== 'trial') {
        const limit = moduleSeatLimits[moduleIdForRole] ?? 9999;
        const used = members.filter(m => m.is_active && m.role === role).length;
        if (used >= limit) {
          setCreateError(`Seat limit reached for ${moduleIdForRole} (${limit} max). Add more seats in Billing.`);
          setCreating(false);
          return;
        }
      }

      const newMember = await createTeamMember({ ...form, role, outlet_ids: (isOwner || isAdmin) ? selectedOutletIds : undefined });
      // Auto-apply role default permissions — but for module-seat roles, skip
      // writing an explicit dbPerms snapshot. Their real permissions come from
      // the module gate (MODULE_FULL_GATES) in getPermissions(), which runs
      // BEFORE dbPerms is applied — saving an all-false snapshot here would
      // override every module-granted permission back to false.
      const isModuleSeatRole = ['ledger_member', 'queue_member', 'employee_member', 'gym_member', 'canteen_member', 'pos_member'].includes(role);
      if (newMember?.id && !isModuleSeatRole) {
        const defaults = { ...DEFAULT_PERMS, ...(ROLE_PERMISSIONS[role as Role] || {}) };
        await savePermissions(newMember.id, defaults);
      }
      // Auto-assign to manager's outlet
      if (isManager && (authUser as any)?.outlet_id && newMember?.id) {
        await fetch(`${BASE}/outlets/${(authUser as any).outlet_id}/members`, {
          method: 'POST', headers,
          body: JSON.stringify({ user_id: newMember.id, role: 'cashier' }),
        });
      }
      setShowCreate(false);
      setForm({ name: '', email_prefix: '', password: '', role: isOwner ? 'staff' : 'cashier' });
      setSelectedOutletIds([]);
      await load(); // refresh members/seat counts first — must not be skipped

      try {
        const { postLog } = await import('../utils/authApi');
        await postLog('IAM_MEMBER_CREATED', `Team member created: ${form.name} | ${form.email_prefix}@cloudairambo.com | Role: ${isOwner ? form.role : 'cashier'}`);
      } catch {
        // audit log is best-effort — never let it block the UI refresh
      }
    } catch (e: any) {
      setCreateError(e.message);
    } finally {
      setCreating(false);
    }
  }

  async function handleResetPassword() {
    if (!resetTarget || newPassword.length < 6) return;
    try {
      setResetting(true);
      await resetPassword(resetTarget.id, newPassword);
      const { postLog } = await import('../utils/authApi');
      await postLog('IAM_PASSWORD_RESET', `Password reset for: ${resetTarget.name} | ${resetTarget.email}`);
      setResetTarget(null);
      setNewPassword('');
    } catch (e: any) { alert(e.message); }
    finally { setResetting(false); }
  }

  async function handleDelete(member: TeamMember) {
    if (!confirm(`Permanently delete ${member.name}? This cannot be undone — they will lose access immediately and this action cannot be reversed.`)) return;
    try {
      await deleteUser(member.id);
      setMembers(prev => prev.filter(m => m.id !== member.id));
      setSelectedMember(null);
      const { postLog } = await import('../utils/authApi');
      await postLog('IAM_MEMBER_DELETED', `Member permanently deleted: ${member.name} | ${member.email}`);
    } catch (e: any) { alert(e.message); }
  }

  async function handleToggleAdmin(member: TeamMember) {
    const newRole = member.role;
    setMembers(prev => prev.map(m => m.id === member.id ? { ...m, role: newRole } : m));
    setSelectedMember(prev => prev?.id === member.id ? { ...prev, role: newRole } : prev);
  }

  function handlePermsSaved(userId: number, perms: Record<string, boolean>) {
    setMembers(prev => prev.map(m => m.id === userId ? { ...m, permissions: perms } : m));
    setSelectedMember(prev => prev?.id === userId ? { ...prev, permissions: perms } : prev);
  }
// ── Outlet functions ─────────────────────────────────────────────────────
  const handleCreateOutletMember = async () => {
    if (!outletMemberForm.name.trim() || !outletMemberForm.email_prefix.trim() || !outletMemberForm.password) {
      setOutletMemberError('All fields are required');
      return;
    }
    try {
      setCreatingOutletMember(true);
      setOutletMemberError('');
      const newMember = await createTeamMember({ ...outletMemberForm, role: outletMemberForm.role });
      if (newMember?.id) {
        const defaults = { ...DEFAULT_PERMS, ...(ROLE_PERMISSIONS[outletMemberForm.role as Role] || {}) };
        await savePermissions(newMember.id, defaults);
      }
      setShowCreateOutletMember(false);
      setOutletMemberForm({ name: '', email_prefix: '', password: '', role: 'cashier' });
      await load();
      if (selectedOutlet) await loadOutletMembers(selectedOutlet.id);
    } catch (e: any) {
      setOutletMemberError(e.message);
    } finally {
      setCreatingOutletMember(false);
    }
  };

  const loadOutlets = async () => {
    setOutletsLoading(true);
    try {
      const res = await fetch(`${BASE}/outlets`, { headers });
      const data = await res.json();
      setOutlets(data);
    } catch (e) { console.error(e); }
    finally { setOutletsLoading(false); }
  };

  const loadOutletMembers = async (outletId: number) => {
    setOutletMembersLoading(true);
    try {
      const res = await fetch(`${BASE}/outlets/${outletId}/members`, { headers });
      const data = await res.json();
      setOutletMembers(data);
    } catch (e) { console.error(e); }
    finally { setOutletMembersLoading(false); }
  };

  const handleSelectOutlet = (outlet: Outlet) => {
    setSelectedOutlet(outlet);
    loadOutletMembers(outlet.id);
  };

  const handleCreateOutlet = async () => {
    if (!outletForm.name.trim()) return alert('Outlet name required');
    if (outlets.some(o => o.name.trim().toLowerCase() === outletForm.name.trim().toLowerCase())) {
      return alert(`An outlet named "${outletForm.name.trim()}" already exists in your organization. Choose a different name.`);
    }
    if (outletForm.modules.length === 0) return alert('Select at least one module for this outlet');
    // Per-module check — mirrors the checkbox list above, not a combined total.
    const fullModules = outletForm.modules.filter(modId => {
      if (modId === 'suite') {
        const used = outlets.filter(o => !!o.suite_tier).length;
        return used >= 1;
      }
      if (modId === 'queue') return false; // Queue never blocks — see checkbox comment above
      const isTrial = moduleStatuses[modId as ModuleId] === 'trial';
      const cap = isTrial ? Infinity : (moduleOutletCounts[modId] ?? 1);
      const used = outlets.filter(o => o.modules?.includes(modId)).length;
      return used >= cap;
    });
    if (fullModules.length > 0) {
      window.dispatchEvent(new CustomEvent('show-upgrade-plans'));
      return;
    }
    setCreatingOutlet(true);
    try {
      const res = await fetch(`${BASE}/outlets`, {
        method: 'POST', headers,
        body: JSON.stringify(outletForm),
      });
      const data = await res.json();
      if (data.id) {
        setShowCreateOutlet(false);
        const { postLog } = await import('../utils/authApi');
        await postLog('OUTLET_CREATED', `Outlet created: ${outletForm.name} | ${outletForm.city || ''} | ${outletForm.country} | ${outletForm.currency} | Modules: ${outletForm.modules.join(', ')}`);
        if (data.modules_failed?.length) {
          alert(`Outlet created, but these modules are at their outlet cap and weren't linked: ${data.modules_failed.join(', ')}. Upgrade outlet count in Billing to add them.`);
        }
        // Broadcast the FULL new outlet so Profile/Product Catalog pick it up immediately.
        window.dispatchEvent(new CustomEvent('outlet-location-updated', { detail: { outletId: data.id, outlet: data, country: outletForm.country, region: outletForm.region } }));
        setOutletForm({ name: '', city: '', country: '', region: '', currency: '', invoice_prefix: '', modules: [] });
        await loadOutlets();
      } else alert(data.detail || 'Failed');
    } catch (e: any) { alert(e.message); }
    finally { setCreatingOutlet(false); }
  };

  const handleSaveEditOutlet = async () => {
    if (!editOutletId) return;
    if (!editOutletForm.name.trim()) return alert('Outlet name required');
    setSavingEditOutlet(true);
    try {
      const res = await fetch(`${BASE}/outlets/${editOutletId}`, {
        method: 'PATCH', headers,
        body: JSON.stringify({
          name: editOutletForm.name.trim(),
          city: editOutletForm.city,
          country: editOutletForm.country,
          region: editOutletForm.region,
          currency: editOutletForm.currency,
          tax_id: editOutletForm.tax_id,
        }),
      });
      const data = await res.json();
      if (data.id) {
        setOutlets(prev => prev.map(o => (o.id === data.id ? data : o)));
        if (selectedOutlet?.id === data.id) setSelectedOutlet(data);
        // Broadcast the FULL updated outlet (name, currency, country, region, etc.)
        window.dispatchEvent(new CustomEvent('outlet-location-updated', { detail: { outletId: data.id, outlet: data, country: editOutletForm.country, region: editOutletForm.region } }));
        setShowEditOutlet(false);
      } else {
        alert(data.detail || 'Failed to update outlet');
      }
    } catch (e: any) {
      alert(e.message);
    } finally {
      setSavingEditOutlet(false);
    }
  };

  // Picks up country/region changes saved from ANY screen (Profile's
  // Country & State card, Product Catalog's picker) while this component
  // is mounted, so the outlet list here never shows stale data.
  useEffect(() => {
    const handler = (e: any) => {
      const o = e.detail?.outlet;
      setOutlets(prev => {
        // New outlet created elsewhere (not yet in this list) — add it.
        if (o && !prev.some(x => x.id === e.detail?.outletId)) return [...prev, o];
        return prev.map(x => {
          if (x.id !== e.detail?.outletId) return x;
          return o ? { ...x, ...o } : { ...x, country: e.detail.country, region: e.detail.region };
        });
      });
      if (selectedOutlet?.id === e.detail?.outletId) {
        setSelectedOutlet(prev => prev ? (o ? { ...prev, ...o } : { ...prev, country: e.detail.country, region: e.detail.region }) : prev);
      }
    };
    window.addEventListener('outlet-location-updated', handler);
    return () => window.removeEventListener('outlet-location-updated', handler);
  }, [selectedOutlet]);

  const handleAddMemberToOutlet = async () => {
    if (!selectedOutlet || !addMemberOutletUserId) return;
    try {
      const res = await fetch(`${BASE}/outlets/${selectedOutlet.id}/members`, {
        method: 'POST', headers,
        body: JSON.stringify({ user_id: parseInt(addMemberOutletUserId), role: addMemberOutletRole }),
      });
      const data = await res.json();
      if (data.message) {
        setAddMemberOutletUserId('');
        const selectedUser = members.find(m => m.id === parseInt(addMemberOutletUserId));
        const { postLog } = await import('../utils/authApi');
        await postLog('OUTLET_MEMBER_ADDED', `Member added to ${selectedOutlet.name}: ${selectedUser?.name || `#${addMemberOutletUserId}`} | Role: ${addMemberOutletRole}`);
        await loadOutletMembers(selectedOutlet.id);
      } else alert(data.detail || 'Failed');
    } catch (e: any) { alert(e.message); }
  };
const handleSaveWhatsappPort = async () => {
    if (!editingPort || !selectedOutlet) return;

    // Validate phone number format (7 to 15 digits)
    const rawNumber = editingPort.number ? editingPort.number.trim() : '';
    const cleanNumber = rawNumber ? getCleanE164Phone(rawNumber, selectedOutlet.country) : '';

    if (rawNumber && !cleanNumber) {
      alert('Please enter a valid phone number (between 7 and 15 digits).');
      return;
    }

    setSavingPort(true);
    try {
      const res = await fetch(`${BASE}/outlets/${editingPort.outletId}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ 
          // Preserve existing port instead of wiping it out to null
          whatsapp_port: editingPort.port ? parseInt(editingPort.port) : (selectedOutlet.whatsapp_port ?? null),
          whatsapp_number: cleanNumber || null,
        }),
      });
      const data = await res.json();
      if (data.id) {
        setOutlets(prev => prev.map(o => o.id === data.id ? data : o));
        setSelectedOutlet(data);
        const { postLog } = await import('../utils/authApi');
        await postLog('OUTLET_WHATSAPP_UPDATED', `WhatsApp number updated for ${selectedOutlet.name}: ${cleanNumber || 'cleared'}`);
        setEditingPort(null);
      } else alert(data.detail || 'Failed');
    } catch (e: any) { alert(e.message); }
    finally { setSavingPort(false); }
  };


  const handleRemoveMemberFromOutlet = async (userId: number) => {
    if (!selectedOutlet || !confirm('Remove this member from outlet?')) return;
    try {
      await fetch(`${BASE}/outlets/${selectedOutlet.id}/members/${userId}`, { method: 'DELETE', headers });
      const removedMember = outletMembers.find(m => m.user_id === userId);
      const { postLog } = await import('../utils/authApi');
      await postLog('OUTLET_MEMBER_REMOVED', `Member removed from ${selectedOutlet.name}: ${removedMember?.name || `#${userId}`}`);
      await loadOutletMembers(selectedOutlet.id);
    } catch (e: any) { alert(e.message); }
  };

useEffect(() => {
  if (activeTab === 'outlets') {
    loadOutlets();
    if (members.length === 0) load();
    // Auto-select manager's outlet
    if (isManager && (authUser as any)?.outlet_id && outlets.length > 0) {
      const myOutlet = outlets.find(o => o.id === (authUser as any).outlet_id);
      if (myOutlet) handleSelectOutlet(myOutlet);
    }
  }
}, [activeTab]);

// Also auto-select when outlets load
useEffect(() => {
  if (isManager && (authUser as any)?.outlet_id && outlets.length > 0 && !selectedOutlet) {
    const myOutlet = outlets.find(o => o.id === (authUser as any).outlet_id);
    if (myOutlet) handleSelectOutlet(myOutlet);
  }
}, [outlets]);


  const filtered = members.filter(m =>
    m.name?.toLowerCase().includes(search.toLowerCase()) ||
    m.email?.toLowerCase().includes(search.toLowerCase())
  );

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '8px 12px', borderRadius: '6px',
    border: '1px solid #e5e7eb', fontSize: '13px', outline: 'none',
    background: '#fff', boxSizing: 'border-box', color: '#111827',
  };

  // If the selected role is a module-scoped role (queue_member, pos_member, etc),
  // only outlets actually assigned to that module should be selectable —
  // ticking an unrelated outlet would give a false sense of access.
  const selectedModuleId = ROLE_TO_MODULE_ID[form.role];
  const moduleFilteredOutlets = selectedModuleId
    ? outlets.filter(o => o.modules?.includes(selectedModuleId))
    : outlets;

  // Live duplicate-name check for the Create Outlet modal — case-insensitive,
  // trimmed, scoped to this org since `outlets` only ever holds this org's
  // rows (backend already filters by org_id in GET /outlets).
  const trimmedOutletName = outletForm.name.trim().toLowerCase();
  const isDuplicateOutletName = trimmedOutletName.length > 0 &&
    outlets.some(o => o.name.trim().toLowerCase() === trimmedOutletName);
if (!canManage && !canManageStaff && !isManager && !perms.canAccessTeam) {
  return (
    <div style={{ textAlign: 'center', padding: '80px', color: '#9ca3af', fontSize: '14px' }}>
      You don't have permission to view team management.
    </div>
  );
}
  return (
    <>
    <style>{`
      @media (max-width: 768px) {
        .iam-outlets-layout { grid-template-columns: 1fr !important; }
      }
    `}</style>
    <div style={{ 
      height: '100%', 
      overflowY: 'auto', 
      background: '#f8fafc', 
      fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' 
    }}>
      <div style={{ maxWidth: '860px', margin: '0 auto', padding: '32px 24px' }}>

        {/* Tab switcher */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
          {(() => {
        const tabs = isManager
  ? ['outlets']
  : ['team', 'outlets'];
          return (tabs as ('team'|'outlets')[]).map(tab => (
            <button key={tab} onClick={() => setActiveTab(tab)} style={{
              padding: '8px 20px', borderRadius: 8, cursor: 'pointer',
              fontWeight: 700, fontSize: 13,
              background: activeTab === tab ? 'linear-gradient(to right,#3b82f6,#9333ea)' : '#fff',
              color: activeTab === tab ? '#fff' : '#6b7280',
              boxShadow: activeTab === tab ? '0 2px 8px rgba(59,130,246,0.3)' : '0 1px 3px rgba(0,0,0,0.06)',
              border: activeTab === tab ? 'none' : '1px solid #e5e7eb',
            }}>
              {tab === 'team' ? '👥 Team Members' : '🏪 Outlets'}
            </button>
          ));
        })()}
        </div>
{/* Plan Usage Summary */}
{activeTab === 'team' && (() => {
  const plan = (authUser as any)?.plan || 'basic';
  const tier = (authUser as any)?.plan_tier || 'individual';

  const activeModuleIds = (Object.entries(moduleStatuses || {}) as [string, ModuleStatus][])
    .filter(([, s]) => s === 'active' || s === 'trial')
    .map(([id]) => id);
  const hasActiveModule = activeModuleIds.length > 0;
  const lockedModuleIds = (Object.entries(moduleStatuses || {}) as [string, ModuleStatus][])
    .filter(([, s]) => s === 'locked')
    .map(([id]) => id);

  const perModuleSeatUsage = activeModuleIds.map(id => ({
    id,
    role: MODULE_MEMBER_ROLE_BY_MODULE[id],
    limit: moduleStatuses?.[id] === 'trial' ? Infinity : (moduleSeatLimits[id] ?? 2),
    used: members.filter(m => m.is_active && m.role === MODULE_MEMBER_ROLE_BY_MODULE[id]).length,
  }));
  const totalModuleSeatLimit = perModuleSeatUsage.reduce((sum, m) => sum + m.limit, 0);
  const totalModuleSeatUsed  = perModuleSeatUsage.reduce((sum, m) => sum + m.used, 0);

  const baseSuiteSeatLimit = 1;

  const effectiveSuiteSeatLimit = suiteSeatLimit ?? baseSuiteSeatLimit;
  const seatLimit = hasActiveModule ? totalModuleSeatLimit : effectiveSuiteSeatLimit;
  const seatUsed  = hasActiveModule ? totalModuleSeatUsed  : members.filter(m => m.is_active).length;

  const outletLimit = moduleOutletLimit !== null ? moduleOutletLimit : 1;
  return (
    <div style={{ marginBottom: 20 }}>
      <PlanUsageBar label="Seats" used={seatUsed} limit={seatLimit} color="#6366f1" />
      {seatUsed > seatLimit && (
        <div style={{ fontSize: 11, color: '#dc2626', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '6px 10px', marginTop: -6, marginBottom: 12, fontWeight: 600 }}>
          ⚠️ You have {seatUsed} team members but your active plan only covers {seatLimit} seats. Members under locked modules have lost access until renewed.
        </div>
      )}
      {hasActiveModule && perModuleSeatUsage.length > 1 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: -6, marginBottom: 12 }}>
          {perModuleSeatUsage.map(m => (
            <span key={m.id} style={{ fontSize: 10, color: '#64748b', background: '#f1f5f9', borderRadius: 6, padding: '2px 8px' }}>
              {MODULE_ICONS[m.id]?.label || m.id}: {m.used}/{m.limit}
            </span>
          ))}
        </div>
      )}

      {lockedModuleIds.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: -6, marginBottom: 12 }}>
          {lockedModuleIds.map(id => (
            <span key={id} style={{ fontSize: 10, color: '#dc2626', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '2px 8px', fontWeight: 700 }}>
              🔒 {MODULE_ICONS[id]?.label || id}: seats & outlets locked — renew to restore
            </span>
          ))}
        </div>
      )}


      <PlanUsageBar label="Outlets" used={outlets.filter(o => !o.locked).length} limit={outletLimit} color="#10b981" />
      {outlets.filter(o => !o.locked).length > outletLimit && (
        <div style={{ fontSize: 11, color: '#dc2626', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, padding: '6px 10px', marginTop: -6, marginBottom: 12, fontWeight: 600 }}>
          ⚠️ You have {outlets.filter(o => !o.locked).length} outlets but your active plan only covers {outletLimit}. Locked modules' outlets are inaccessible until renewed.
        </div>
      )}
{moduleOutletLimit !== null && (() => {
  const activeModuleIds = (Object.entries(moduleStatuses || {}) as [string, ModuleStatus][])
    .filter(([, s]) => s === 'active' || s === 'trial')
    .map(([id]) => id);
  if (activeModuleIds.length <= 1) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: -6, marginBottom: 12 }}>
      {activeModuleIds.map(id => (
        <span key={id} style={{ fontSize: 10, color: '#64748b', background: '#f1f5f9', borderRadius: 6, padding: '2px 8px' }}>
          {MODULE_ICONS[id]?.label || id}: +{moduleOutletCounts[id] ?? 1} outlet{(moduleOutletCounts[id] ?? 1) > 1 ? 's' : ''}
        </span>
      ))}
    </div>
  );
})()}
      <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 8 }}>
        {hasActiveModule ? (
          <>
            Plan: <strong style={{ color: '#1e293b' }}>Standalone Modules</strong>
          </>
        ) : (
          <>
            Plan: <strong style={{ color: '#1e293b', textTransform: 'capitalize' }}>{plan} · {tier}</strong>
          </>
        )}
        {' · '}
        <span style={{ color: '#6366f1', cursor: 'pointer', fontWeight: 600 }}
          onClick={() => window.dispatchEvent(new CustomEvent('show-upgrade-plans'))}>
          Upgrade ↗
        </span>
      </div>
   {(() => {
  const activeModules: { id: ModuleId; label: string; status: ModuleStatus }[] = [
    { id: 'ledger',    label: '📒 Credit Book',    status: moduleStatuses?.ledger    ?? 'inactive' },
    { id: 'employees', label: '👥 Attendance', status: moduleStatuses?.employees ?? 'inactive' },
    { id: 'queue',     label: '🎫 Queue',     status: moduleStatuses?.queue     ?? 'inactive' },
    { id: 'gym',       label: '🏋️ Fitness Center', status: moduleStatuses?.gym       ?? 'inactive' },
    { id: 'canteen',   label: '🍽️ Meal Plans & Dining', status: moduleStatuses?.canteen   ?? 'inactive' },
    { id: 'pos',       label: '🧾 POS',       status: moduleStatuses?.pos       ?? 'inactive' },
  ].filter(m => m.status !== 'inactive');
  if (!activeModules.length) return null;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
      <span style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', alignSelf: 'center' }}>Modules:</span>
      {activeModules.map(m => (
        <span key={m.id} style={{
          fontSize: 10, fontWeight: 700, borderRadius: 99, padding: '2px 10px',
          background: m.status === 'locked' ? '#fef2f2' : '#f0fdf4',
          color: m.status === 'locked' ? '#dc2626' : '#16a34a',
          border: `1px solid ${m.status === 'locked' ? '#fecaca' : '#bbf7d0'}`,
        }}>
          {m.status === 'locked' ? '🔒' : '✓'} {m.label} · {m.status}
        </span>
      ))}
    </div>
  );
})()}
    </div>
  );
})()}
 
        {activeTab === 'team' && <>
        {/* Page Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '28px', flexWrap: 'wrap', gap: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{ width: '42px', height: '42px', borderRadius: '10px', background: 'linear-gradient(135deg, #3b82f6, #9333ea)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Users size={20} color="#fff" />
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 700, color: '#111827' }}>Team Management</h2>
              <p style={{ margin: 0, fontSize: '13px', color: '#6b7280' }}>Click a user to view and manage their permissions</p>
            </div>
          </div>
          {canManageStaff && (
            <button
              onClick={() => {
                const activeModuleIds: ModuleId[] = (['ledger','queue','employees','gym','canteen','pos'] as ModuleId[])
  .filter(id => ({ ledger: hasLedger, queue: hasQueue, employees: hasEmployees, gym: hasGym, canteen: hasCanteen, pos: hasPos }[id]));

                if (activeModuleIds.length > 0) {
                  // Only block if EVERY active module is genuinely full — never guess which one the owner wants.
                  const allFull = activeModuleIds.every(id => {
                    if (moduleStatuses[id] === 'trial') return false; // trial = never full
                    const targetRole = MODULE_MEMBER_ROLE_BY_MODULE[id];
                    const seatLimit = moduleSeatLimits[id] ?? 9999;
                    const activeCount = members.filter(m => m.is_active && m.role === targetRole).length;
                    return activeCount >= seatLimit;
                  });
                  if (allFull) {
                    window.dispatchEvent(new CustomEvent('show-upgrade-plans'));
                    return;
                  }
                  setForm(f => ({ ...f, role: activeModuleIds.length === 1 ? MODULE_MEMBER_ROLE_BY_MODULE[activeModuleIds[0]] : f.role }));
                  setShowCreate(true);
                  return;
                }

                const baseLimit = 1;
                const seatLimit = suiteSeatLimit ?? baseLimit;
                const activeCount = members.filter(m => m.is_active).length;
                if (activeCount >= seatLimit) {
                  window.dispatchEvent(new CustomEvent('show-upgrade-plans'));
                  return;
                }
                setShowCreate(true);
              }}
              style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '9px 18px', borderRadius: '8px', border: 'none', background: 'linear-gradient(to right, #3b82f6, #9333ea)', color: '#fff', fontWeight: 600, fontSize: '14px', cursor: 'pointer' }}
            >
              <Plus size={16} /> Add Member {(() => {

  const activeModuleIds: ModuleId[] = (['ledger','queue','employees','gym','canteen','pos'] as ModuleId[])
    .filter(id => ({ ledger: hasLedger, queue: hasQueue, employees: hasEmployees, gym: hasGym, canteen: hasCanteen, pos: hasPos }[id]));

  if (activeModuleIds.length === 1) {
    const id = activeModuleIds[0];
    const targetRole = MODULE_MEMBER_ROLE_BY_MODULE[id];
    const limit = moduleStatuses[id] === 'trial' ? Infinity : (moduleSeatLimits[id] ?? 2);
    const used = members.filter(m => m.is_active && m.role === targetRole).length;
    return `(${used}/${limit === Infinity ? '∞' : limit})`;
  }
  if (activeModuleIds.length > 1) {
    // Multiple independent seat pools — a single combined number would misrepresent
    // what's actually available per module. See the per-module pills above instead.
    return '';
  }
  const baseLimit = 1;
  const limit = suiteSeatLimit ?? baseLimit;
  const used = members.filter(m => m.is_active).length;
  return `(${used}/${limit === Infinity ? '∞' : limit})`;
})()}
            </button>
          )}
        </div>

        {/* Members Table */}
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px', borderBottom: '1px solid #e5e7eb', background: '#f8fafc', flexWrap: 'wrap', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Users size={15} color="#6b7280" />
              <span style={{ fontSize: '13px', fontWeight: 700, color: '#111827', letterSpacing: '0.04em' }}>USERS ({filtered.length})</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: '1 1 auto', minWidth: 0, justifyContent: 'flex-end' }}>
              <div style={{ position: 'relative', flex: '1 1 auto', minWidth: 120, maxWidth: 200 }}>
                <Search size={13} color="#9ca3af" style={{ position: 'absolute', left: '9px', top: '50%', transform: 'translateY(-50%)' }} />
                <input style={{ ...inputStyle, paddingLeft: '30px', width: '100%' }} placeholder="Search users..." value={search} onChange={e => setSearch(e.target.value)} />
              </div>
              <button onClick={load} style={{ padding: '7px', borderRadius: '6px', border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', color: '#6b7280' }} title="Refresh">
                <RefreshCw size={13} />
              </button>
            </div>
          </div>

          <div className="iam-table-wrap" style={{ overflowX: 'auto' }}>
          <div className="iam-table-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto auto', padding: '8px 20px', background: '#fafafa', borderBottom: '1px solid #f1f5f9', minWidth: 560 }}>
            {['User', 'Email', 'Permissions', 'Status'].map(h => (
              <div key={h} style={{ fontSize: '11px', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</div>
            ))}
          </div>

          {error && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 20px', background: '#fef2f2', color: '#dc2626', fontSize: '13px' }}>
              <AlertCircle size={14} /> {error}
            </div>
          )}

          {loading ? (
            <div style={{ textAlign: 'center', padding: '48px', color: '#9ca3af', fontSize: '14px' }}>Loading team...</div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '48px', color: '#9ca3af', fontSize: '14px' }}>
              {search ? 'No users match your search.' : 'No team members yet.'}
            </div>
          ) : (
            filtered.map((member, i) => {
              const isOwnerRow = member.role === 'owner';
              const isAdminRow = member.role === 'admin';
              const grantedCount = isOwnerRow ? PERM_LABELS.length : Object.values(member.permissions || {}).filter(Boolean).length;
              const isSelected = selectedMember?.id === member.id;
              const isLockedOut = isMemberModuleLocked(member.role, moduleStatuses);
              return (
                <div
                  key={member.id}
                  onClick={() => {
  const canClick = (canManage || (isManager && member.outlet_id === (authUser as any)?.outlet_id)) && !isLockedOut;
  if (canClick) setSelectedMember(isSelected ? null : member);
}}
                  className="iam-table-row"
                  style={{
                    display: 'grid', gridTemplateColumns: '1fr 1fr auto auto',
                    alignItems: 'center', padding: '13px 20px', minWidth: 560,
                    borderBottom: i < filtered.length - 1 ? '1px solid #f9fafb' : 'none',
                    cursor: ((canManage || (isManager && member.outlet_id === (authUser as any)?.outlet_id)) && !isLockedOut) ? 'pointer' : 'not-allowed',
                    background: isSelected ? '#eff6ff' : 'transparent',
                    transition: 'background 0.1s',
                    opacity: member.is_active ? (isLockedOut ? 0.55 : 1) : 0.5,
                  }}
                  onMouseEnter={e => { if (!isSelected && canManage) (e.currentTarget as HTMLDivElement).style.background = '#f8fafc'; }}
                  onMouseLeave={e => { if (!isSelected) (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{
                      width: '32px', height: '32px', borderRadius: '6px',
                      background: isOwnerRow ? 'linear-gradient(135deg, #f59e0b, #ef4444)' : 'linear-gradient(135deg, #3b82f6, #9333ea)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#fff', fontWeight: 700, fontSize: '13px', flexShrink: 0,
                    }}>
                      {member.name?.charAt(0).toUpperCase()}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' as const }}>
                      <span style={{ fontWeight: 600, fontSize: '13px', color: '#111827' }}>{member.name}</span>
                      {isOwnerRow && <Crown size={12} color="#d97706" />}
                      {isAdminRow && <ShieldCheck size={12} color="#3b82f6" />}
                      {(() => {
                        const modId = ROLE_TO_MODULE_ID[member.role];
                        const info = modId ? MODULE_ICONS[modId] : null;
                        if (!info) return null;
                        return (
                          <span style={{
                            fontSize: 9, fontWeight: 800, padding: '2px 7px', borderRadius: 99,
                            background: '#f5f3ff', color: '#7c3aed', border: '1px solid #ddd6fe',
                            display: 'inline-flex', alignItems: 'center', gap: 3, whiteSpace: 'nowrap' as const,
                          }}>
                            {info.icon} {info.label} seat
                          </span>
                        );
                      })()}
                    </div>
                  </div>

                  <span style={{ fontSize: '13px', color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: '12px' }}>
                    {member.email}
                  </span>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginRight: '16px' }}>
                    <Shield size={13} color={grantedCount > 0 ? '#3b82f6' : '#d1d5db'} />
                    <span style={{ fontSize: '12px', fontWeight: 600, color: grantedCount > 0 ? '#2563eb' : '#9ca3af' }}>
                      {grantedCount}/{PERM_LABELS.length}
                    </span>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {isLockedOut ? (
                      <span style={{ fontSize: '10px', fontWeight: 700, color: '#dc2626', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 99, padding: '2px 8px' }}>
                        🔒 Locked
                      </span>
                    ) : (
                      <>
                        <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: member.is_active ? '#10b981' : '#d1d5db' }} />
                        <span style={{ fontSize: '12px', color: member.is_active ? '#059669' : '#9ca3af', fontWeight: 500 }}>
                          {member.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </>
                    )}
                    {canManage && !isLockedOut && <ChevronRight size={14} color={isSelected ? '#3b82f6' : '#d1d5db'} style={{ transition: 'transform 0.15s', transform: isSelected ? 'rotate(90deg)' : 'none' }} />}
                  </div>
                </div>
              );
            })
          )}
        </div>
        </div>
     
</> /* end activeTab === 'team' */}

        {/* ── Outlets Tab ── */}
        {activeTab === 'outlets' && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 42, height: 42, borderRadius: 10, background: 'linear-gradient(135deg,#f59e0b,#ef4444)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>🏪</div>
                <div>
                  <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: '#111827' }}>Outlets</h2>
                  <p style={{ margin: 0, fontSize: 13, color: '#6b7280' }}>Manage branches and assign staff to each outlet</p>
                </div>
              </div>
              {isOwner && (
               <button onClick={() => setShowCreateOutlet(true)} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '9px 18px', borderRadius: 8, border: 'none', background: 'linear-gradient(to right,#f59e0b,#ef4444)', color: '#fff', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
  <Plus size={16} /> Add Outlet ({outlets.filter(o => !o.locked && (o.modules || []).some(m => moduleStatuses[m as ModuleId] === 'active' || moduleStatuses[m as ModuleId] === 'trial')).length}/{getOutletLimit() === Infinity ? '∞' : getOutletLimit()})
</button>
              )}
            </div>

            <div className="iam-outlets-layout" style={{ display: 'grid', gridTemplateColumns: selectedOutlet ? '1fr 1fr' : '1fr', gap: 16 }}>
              {/* Outlet list */}
              <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
                {outletsLoading ? (
                  <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading outlets...</div>
                ) : outlets.length === 0 ? (
                  <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>No outlets yet. Create one to get started.</div>
                ) : outlets.map((outlet, i) => (
                  <div key={outlet.id} onClick={() => handleSelectOutlet(outlet)} style={{
                    padding: '14px 18px', cursor: 'pointer',
                    borderBottom: i < outlets.length - 1 ? '1px solid #f1f5f9' : 'none',
                    background: selectedOutlet?.id === outlet.id ? '#eff6ff' : 'transparent',
                    transition: 'background 0.1s',
                  }}
                    onMouseEnter={e => { if (selectedOutlet?.id !== outlet.id) (e.currentTarget as HTMLElement).style.background = '#f8fafc'; }}
                    onMouseLeave={e => { if (selectedOutlet?.id !== outlet.id) (e.currentTarget as HTMLElement).style.background = 'transparent'; }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                      {(() => {
                        const outletModules = (outlet.modules || []).map(modId => ({
                          modId,
                          status: (moduleStatuses[modId as ModuleId] || 'inactive') as ModuleStatus,
                        }));
                        const badModules = outletModules.filter(m => m.status !== 'active' && m.status !== 'trial');
                        const lockReason = badModules.length > 0
                          ? badModules.map(m => MODULE_ICONS[m.modId]?.label || m.modId).join(', ')
                          : null;

                        return (
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 700, fontSize: 14, color: '#111827', display: 'flex', alignItems: 'center', gap: 6 }}>
                              {outlet.locked && (
                                <span title={lockReason ? `${lockReason} plan expired` : 'Module locked — renew to restore'} style={{ fontSize: 11 }}>🔒</span>
                              )}
                              {outlet.name}
                              <span style={{ display: 'flex', gap: 4, flexWrap: 'wrap' as const, marginLeft: 2 }}>
                                {outlet.suite_tier && (
                                  <span
                                    title={`Dedicated to Individual Suite — ${outlet.suite_tier}`}
                                    style={{
                                      fontSize: 9, fontWeight: 800, padding: '2px 7px', borderRadius: 99,
                                      display: 'inline-flex', alignItems: 'center', gap: 3, whiteSpace: 'nowrap' as const,
                                      background: '#f5f3ff', color: '#7c3aed', border: '1px solid #ddd6fe',
                                    }}
                                  >
                                    💎 {outlet.suite_tier === 'ultra' ? 'Ultra' : outlet.suite_tier === 'pro' ? 'Pro' : 'Basic'}
                                  </span>
                                )}
                                {outletModules.map(m => {
                                  const info = MODULE_ICONS[m.modId];
                                  if (!info) return null;
                                  const isBad = m.status !== 'active' && m.status !== 'trial';
                                  return (
                                    <span
                                      key={m.modId}
                                      title={`${info.label} — ${m.status}${isBad ? ' (renew to restore)' : ''}`}
                                      style={{
                                        fontSize: 9, fontWeight: 800, padding: '2px 7px', borderRadius: 99,
                                        display: 'inline-flex', alignItems: 'center', gap: 3, whiteSpace: 'nowrap' as const,
                                        background: isBad ? '#fef2f2' : '#f0fdf4',
                                        color: isBad ? '#dc2626' : '#16a34a',
                                        border: `1px solid ${isBad ? '#fecaca' : '#bbf7d0'}`,
                                      }}
                                    >
                                      {info.icon} {info.label}
                                    </span>
                                  );
                                })}
                              </span>
                            </div>
                            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                              {outlet.city && `${outlet.city} · `}{outlet.country} · {outlet.currency}
                              {outlet.invoice_prefix && ` · ${outlet.invoice_prefix}-XXXXX`}
                            </div>
                            {outlet.locked && lockReason && (
                              <div style={{ fontSize: 11, color: '#dc2626', marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                                🔒 {lockReason} plan expired
                                <span
                                  style={{ color: '#6366f1', fontWeight: 700, cursor: 'pointer' }}
                                  onClick={e => { e.stopPropagation(); window.dispatchEvent(new CustomEvent('show-upgrade-plans')); }}
                                >
                                  Renew →
                                </span>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                        {outlet.whatsapp_connected && <span style={{ fontSize: 10, background: '#dcfce7', color: '#16a34a', padding: '2px 7px', borderRadius: 99, fontWeight: 700 }}>WA ✓</span>}
                        {outlet.locked && <span style={{ fontSize: 10, background: '#fef2f2', color: '#dc2626', padding: '2px 7px', borderRadius: 99, fontWeight: 700, border: '1px solid #fecaca' }}>🔒 Locked</span>}
                       
                       
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
  {isOwner && !outlet.locked && (
    <button
  onClick={e => {
    e.stopPropagation();
    setEditOutletId(outlet.id);
    setEditOutletForm({
      name: outlet.name || '',
      city: outlet.city || '',
      country: outlet.country || '',
      region: outlet.region || '',
      currency: outlet.currency || '',
      tax_id: outlet.tax_id || '',
    });
    setShowEditOutlet(true);
  }}
  style={{
    background: 'none', border: '1px solid #e5e7eb', borderRadius: 6,
    cursor: 'pointer', color: '#6b7280', padding: '4px 8px',
    fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3,
    transition: 'all 0.15s',
  }}
  onMouseEnter={e => {
    (e.currentTarget as HTMLElement).style.background = '#eff6ff';
    (e.currentTarget as HTMLElement).style.color = '#6366f1';
    (e.currentTarget as HTMLElement).style.borderColor = '#c7d2fe';
  }}
  onMouseLeave={e => {
    (e.currentTarget as HTMLElement).style.background = 'none';
    (e.currentTarget as HTMLElement).style.color = '#6b7280';
    (e.currentTarget as HTMLElement).style.borderColor = '#e5e7eb';
  }}
>
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
  Edit
</button>
  )}
  {isOwner && (
    <button
      onClick={async e => {
        e.stopPropagation();
        const lockedWarning = outlet.locked ? ' This outlet is locked/inactive — deleting it will free up its module slot.' : '';
        if (!confirm(`Delete outlet "${outlet.name}"?${lockedWarning} This cannot be undone.`)) return;
        const res = await fetch(`${BASE}/outlets/${outlet.id}`, { method: 'DELETE', headers });
        if (res.ok) {
          if (selectedOutlet?.id === outlet.id) setSelectedOutlet(null);
          await loadOutlets();
        } else {
          alert('Failed to delete outlet');
        }
      }}
      style={{
  background: 'none', border: '1px solid #e5e7eb', borderRadius: 6,
  cursor: 'pointer', color: '#6b7280', padding: '4px 8px',
  fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3,
  transition: 'all 0.15s',
}}
onMouseEnter={e => {
  (e.currentTarget as HTMLElement).style.background = '#fff5f5';
  (e.currentTarget as HTMLElement).style.color = '#ef4444';
  (e.currentTarget as HTMLElement).style.borderColor = '#fecaca';
}}
onMouseLeave={e => {
  (e.currentTarget as HTMLElement).style.background = 'none';
  (e.currentTarget as HTMLElement).style.color = '#6b7280';
  (e.currentTarget as HTMLElement).style.borderColor = '#e5e7eb';
}}
>
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>
  Delete
</button>
  )}
  <ChevronRight size={14} color={selectedOutlet?.id === outlet.id ? '#3b82f6' : '#d1d5db'} />
</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Outlet detail */}
              {selectedOutlet && (
                <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
                  <div style={{ padding: '14px 18px', borderBottom: '1px solid #e5e7eb', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontWeight: 700, fontSize: 14, color: '#111827' }}>{selectedOutlet.name} — Staff</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {(canManage || isManager) && !selectedOutlet.locked && (
                        <button onClick={() => { setShowCreateOutletMember(true); setOutletMemberError(''); }} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 12px', borderRadius: 6, border: 'none', background: 'linear-gradient(to right,#3b82f6,#9333ea)', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                          <Plus size={13} /> New Member
                        </button>
                      )}
                      <button onClick={() => setSelectedOutlet(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af' }}><X size={16} /></button>
                    </div>
                  </div>
{/* Branch WhatsApp Number */}
{isOwner && !selectedOutlet.locked && (
  <div style={{ padding: '12px 18px', borderBottom: '1px solid #f1f5f9', background: '#fffbeb' }}>
    <div style={{ fontSize: 11, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>📱 Branch WhatsApp Number</div>
    <div style={{ display: 'flex', gap: 8 }}>
      <OutletPhoneInput
        key={selectedOutlet.id}
        defaultCountry={selectedOutlet.country}
        value={editingPort?.outletId === selectedOutlet.id ? (editingPort.number || '') : (selectedOutlet.whatsapp_number || '')}
        onChange={v => setEditingPort({
          outletId: selectedOutlet.id,
          port: selectedOutlet.whatsapp_port ? String(selectedOutlet.whatsapp_port) : '',
          number: v,
        })}
      />
      <button onClick={handleSaveWhatsappPort} disabled={savingPort || !editingPort || editingPort.outletId !== selectedOutlet.id}
        style={{ padding: '6px 14px', borderRadius: 6, border: 'none', background: '#f59e0b', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer', opacity: (savingPort || !editingPort || editingPort.outletId !== selectedOutlet.id) ? 0.5 : 1 }}>
        {savingPort ? '...' : 'Save'}
      </button>
    </div>
    <div style={{ fontSize: 10, color: '#92400e', marginTop: 5 }}>
      This number appears on invoices for this branch. All WhatsApp messages send via the org's connected number.
    </div>
  </div>
)}
             
                  {/* Add member */}
                  {(canManage || (isManager && selectedOutlet?.id === (authUser as any)?.outlet_id)) && !selectedOutlet.locked && (
  <div style={{ padding: '12px 18px', borderBottom: '1px solid #f1f5f9', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <select value={addMemberOutletUserId} onChange={e => setAddMemberOutletUserId(e.target.value)}
                        style={{ flex: '1 1 160px', minWidth: 140, padding: '6px 10px', borderRadius: 6, border: '1px solid #e5e7eb', fontSize: 12, color: '#111827' }}>
                        <option value=''>Select user...</option>
                        {members
  .filter(m => m.role !== 'owner' && (isOwner || isAdmin || m.role === 'cashier' || m.role === 'staff'))
  .map(m => (
    <option key={m.id} value={m.id}>{m.name} ({m.role})</option>
  ))
}
                      </select>
                      <select value={addMemberOutletRole} onChange={e => setAddMemberOutletRole(e.target.value)}
  style={{ flex: '0 1 100px', padding: '6px 10px', borderRadius: 6, border: '1px solid #e5e7eb', fontSize: 12, color: '#111827' }}>
  <option value='cashier'>Cashier</option>
  {canManage && <option value='manager'>Manager</option>}
  <option value='viewer'>Viewer</option>
</select>
                      <button onClick={handleAddMemberToOutlet} style={{ flexShrink: 0, padding: '6px 14px', borderRadius: 6, border: 'none', background: '#3b82f6', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                        Add
                      </button>
                    </div>
                  )}

                  {/* Members list */}
                  {outletMembersLoading ? (
                    <div style={{ padding: 24, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>Loading...</div>
                  ) : outletMembers.length === 0 ? (
                    <div style={{ padding: 24, textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>No staff assigned yet.</div>
                  ) : outletMembers.map((m, i) => (
                    <div key={m.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 18px', borderBottom: i < outletMembers.length - 1 ? '1px solid #f9fafb' : 'none' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 28, height: 28, borderRadius: 6, background: 'linear-gradient(135deg,#3b82f6,#9333ea)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 12 }}>
                          {m.name?.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#111827' }}>{m.name}</div>
                          <div style={{ fontSize: 10, color: '#9ca3af' }}>{m.email}</div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 99, background: m.role === 'manager' ? '#eff6ff' : '#f3f4f6', color: m.role === 'manager' ? '#1d4ed8' : '#6b7280' }}>
                          {m.role.toUpperCase()}
                        </span>
                        {(canManage || (isManager && selectedOutlet?.id === (authUser as any)?.outlet_id && m.role !== 'manager')) && !selectedOutlet.locked && (
  <button onClick={() => handleRemoveMemberFromOutlet(m.user_id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#d1d5db', padding: 2 }}
                            onMouseEnter={e => (e.currentTarget.style.color = '#ef4444')}
                            onMouseLeave={e => (e.currentTarget.style.color = '#d1d5db')}>
                            <X size={13} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
     {selectedMember && (
 <MemberDetailPanel
  member={selectedMember}
  isOwner={isOwner}
  canManage={canManage || (isManager && selectedMember.outlet_id === (authUser as any)?.outlet_id)}
    currentUserId={authUser?.id}
    authUser={authUser}
    onClose={() => setSelectedMember(null)}
    onResetPassword={m => { setResetTarget(m); setNewPassword(''); }}
    onDeactivate={handleDelete}
    onToggleAdmin={handleToggleAdmin}
    onPermsSaved={handlePermsSaved}
    moduleStatuses={moduleStatuses}
    hasLedger={hasLedger}
    hasQueue={hasQueue}
    hasEmployees={hasEmployees}
    hasGym={hasGym}
    hasCanteen={hasCanteen}
    hasPos={hasPos}
    outlets={outlets}
    BASE={BASE}
    headers={headers}
    members={members}
    moduleSeatLimits={moduleSeatLimits}
  />
)}

      {/* Create member modal */}
      {showCreate && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, boxSizing: 'border-box' }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '28px', width: '440px', maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.15)', boxSizing: 'border-box' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 700, color: '#111827' }}>Add Team Member</h3>
              <button onClick={() => setShowCreate(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}><X size={18} /></button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div>
                <label style={{ fontSize: '12px', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Full Name</label>
                <input style={inputStyle} placeholder="John Doe" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
              </div>
              <div>
                <label style={{ fontSize: '12px', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Email</label>
                <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #e5e7eb', borderRadius: '6px', overflow: 'hidden' }}>
                  <input
                    style={{ ...inputStyle, border: 'none', borderRadius: 0, flex: 1 }}
                    placeholder="john"
                    value={form.email_prefix}
                    onChange={e => setForm(f => ({ ...f, email_prefix: e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g, '') }))}
                  />
                  <span style={{ padding: '0 12px', fontSize: '12px', color: '#6b7280', background: '#f8fafc', borderLeft: '1px solid #e5e7eb', whiteSpace: 'nowrap' }}>
                    @cloudairambo.com
                  </span>
                </div>
              </div>
              <div>
                <label style={{ fontSize: '12px', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Password</label>
                <input style={inputStyle} type="password" placeholder="Min 6 characters" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
              </div>
              {isOwner && (() => {
                const MODULE_ROLES = ['ledger_member', 'queue_member', 'employee_member', 'gym_member', 'canteen_member', 'pos_member'];
                const isModuleRole = MODULE_ROLES.includes(form.role);
                const anyModuleActive = hasLedger || hasQueue || hasEmployees || hasGym || hasCanteen || hasPos;

                return (
                  <div>
                    <label style={{ fontSize: '12px', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '5px', textTransform: 'uppercase' as const, letterSpacing: '0.04em' }}>
                      Assign as
                    </label>

                    {/* Toggle: Staff Role vs Module Access */}
                    <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                      <button
                        type="button"
                        onClick={() => setForm(f => ({ ...f, role: 'staff' }))}
                        style={{
                          flex: 1, padding: '8px', borderRadius: 8, cursor: 'pointer',
                          border: `1.5px solid ${!isModuleRole ? '#3b82f6' : '#e5e7eb'}`,
                          background: !isModuleRole ? '#eff6ff' : '#fff',
                          color: !isModuleRole ? '#1d4ed8' : '#6b7280',
                          fontSize: 12, fontWeight: 700,
                        }}
                      >
                        👤 Staff Role
                      </button>
                      {anyModuleActive && (
                        <button
                          type="button"
                          onClick={() => {
                            const firstModuleRole =
  hasLedger ? 'ledger_member' :
  hasQueue ? 'queue_member' :
  hasEmployees ? 'employee_member' :
  hasGym ? 'gym_member' :
  hasCanteen ? 'canteen_member' : 'pos_member';
                            setForm(f => ({ ...f, role: firstModuleRole }));
                          }}
                          style={{
                            flex: 1, padding: '8px', borderRadius: 8, cursor: 'pointer',
                            border: `1.5px solid ${isModuleRole ? '#3b82f6' : '#e5e7eb'}`,
                            background: isModuleRole ? '#eff6ff' : '#fff',
                            color: isModuleRole ? '#1d4ed8' : '#6b7280',
                            fontSize: 12, fontWeight: 700,
                          }}
                        >
                          🧩 Module Access
                        </button>
                      )}
                    </div>

                    {/* Staff role dropdown */}
                    {!isModuleRole && (
                      <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))} style={inputStyle}>
                        <option value="cashier">Cashier </option>
                        <option value="staff">Staff </option>
                        <option value="manager">Manager </option>
                        <option value="admin">Admin </option>
                        <option value="viewer">Viewer </option>
                      </select>
                    )}

                    {/* Module role checklist — shows used/cap, greys out full modules */}
                    {isModuleRole && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, border: '1px solid #e5e7eb', borderRadius: 6, padding: '8px 10px' }}>
                        {[
                          { role: 'ledger_member', modId: 'ledger', label: '📒 Credit Book Member', active: hasLedger },
                          { role: 'queue_member', modId: 'queue', label: '🎫 Queue Member', active: hasQueue },
                          { role: 'employee_member', modId: 'employees', label: '👥 Attendance Member', active: hasEmployees },
                          { role: 'gym_member', modId: 'gym', label: '🏋️ Fitness Center Member', active: hasGym },
                          { role: 'canteen_member', modId: 'canteen', label: '🍽️ Meal Plans & Dining Member', active: hasCanteen },
                          { role: 'pos_member', modId: 'pos', label: '🧾 POS Member', active: hasPos },
                        ].filter(m => m.active).map(m => {
                          const isTrial = moduleStatuses[m.modId as ModuleId] === 'trial';
                          const cap = isTrial ? Infinity : (moduleSeatLimits[m.modId] ?? 2);
                          const used = members.filter(mm => mm.is_active && mm.role === m.role).length;
                          const isSelected = form.role === m.role;
                          const isFull = !isSelected && used >= cap;
                          return (
                            <label key={m.role} style={{
                              display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                              fontSize: 13, color: isFull ? '#9ca3af' : '#374151',
                              cursor: isFull ? 'not-allowed' : 'pointer', opacity: isFull ? 0.6 : 1,
                            }}>
                              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <input
                                  type="radio"
                                  name="module-role"
                                  checked={isSelected}
                                  disabled={isFull}
                                  onChange={() => { if (!isFull) setForm(f => ({ ...f, role: m.role })); }}
                                />
                                {m.label}
                              </span>
                              <span style={{ fontSize: 10, fontWeight: 700, color: isFull ? '#dc2626' : '#94a3b8' }}>
                                {used}/{cap === Infinity ? '∞' : cap}{isFull ? ' · full' : ''}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })()}
              {(isOwner || isAdmin) && outlets.length > 0 && (
                <div>
                  <label style={{ fontSize: '12px', fontWeight: 600, color: '#374151', display: 'block', marginBottom: '5px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    Outlet Access
                  </label>
                  {moduleFilteredOutlets.length === 0 ? (
                    <div style={{ padding: '10px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, fontSize: 12, color: '#dc2626', fontWeight: 600 }}>
                      ⚠️ No outlet has been assigned to the {MODULE_ICONS[selectedModuleId!]?.label || selectedModuleId} module yet. Create or edit an outlet under the Outlets tab and assign it to this module before adding a member here.
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, border: '1px solid #e5e7eb', borderRadius: 6, padding: '8px 10px', maxHeight: 140, overflowY: 'auto' }}>
                      {moduleFilteredOutlets.map(o => (
                        <label key={o.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#374151', cursor: 'pointer' }}>
                          <input
                            type="checkbox"
                            checked={selectedOutletIds.includes(o.id)}
                            onChange={() => {
                              setSelectedOutletIds(prev =>
                                prev.includes(o.id) ? prev.filter(id => id !== o.id) : [...prev, o.id]
                              );
                            }}
                          />
                          {o.name}
                        </label>
                      ))}
                    </div>
                  )}
                  {moduleFilteredOutlets.length > 0 && selectedOutletIds.length === 0 && (
                    <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>
                      No outlets selected — they won't have access to any outlet until assigned.
                    </div>
                  )}
                </div>
              )}
              {!isOwner && (
                <div style={{ padding: '8px 10px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, fontSize: 11, color: '#166534', fontWeight: 600 }}>
                  ✓ New member will be assigned as Cashier to your outlet automatically
                </div>
              )}
              {createError && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#dc2626', fontSize: '13px' }}>
                  <AlertCircle size={14} /> {createError}
                </div>
              )}
              <button
                onClick={handleCreate}
                disabled={creating || (!!selectedModuleId && moduleFilteredOutlets.length === 0) || (!!selectedModuleId && selectedOutletIds.length === 0)}
                style={{
                  padding: '10px', borderRadius: '8px', border: 'none',
                  background: 'linear-gradient(to right, #3b82f6, #9333ea)', color: '#fff',
                  fontWeight: 600, fontSize: '14px',
                  cursor: (creating || (!!selectedModuleId && moduleFilteredOutlets.length === 0) || (!!selectedModuleId && selectedOutletIds.length === 0)) ? 'not-allowed' : 'pointer',
                  opacity: (creating || (!!selectedModuleId && moduleFilteredOutlets.length === 0) || (!!selectedModuleId && selectedOutletIds.length === 0)) ? 0.5 : 1,
                }}
              >
                {creating
                  ? 'Creating...'
                  : (!!selectedModuleId && moduleFilteredOutlets.length === 0)
                    ? 'No matching outlet'
                    : (!!selectedModuleId && selectedOutletIds.length === 0)
                      ? 'Select an outlet'
                      : 'Create Account'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset password modal */}
      {resetTarget && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, boxSizing: 'border-box' }}>
          <div style={{ background: '#fff', borderRadius: '12px', padding: '28px', width: '380px', maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.15)', boxSizing: 'border-box' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 700, color: '#111827' }}>Reset Password</h3>
              <button onClick={() => setResetTarget(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}><X size={18} /></button>
            </div>
            <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: '#6b7280' }}>
              Set a new password for <strong>{resetTarget.name}</strong>
            </p>
            <input style={{ ...inputStyle, marginBottom: '14px' }} type="password" placeholder="New password (min 6 chars)" value={newPassword} onChange={e => setNewPassword(e.target.value)} />
            <button
              onClick={handleResetPassword}
              disabled={resetting || newPassword.length < 6}
              style={{ width: '100%', padding: '10px', borderRadius: '8px', border: 'none', background: 'linear-gradient(to right, #3b82f6, #9333ea)', color: '#fff', fontWeight: 600, fontSize: '14px', cursor: 'pointer', opacity: resetting || newPassword.length < 6 ? 0.6 : 1 }}
            >
              {resetting ? 'Saving...' : 'Update Password'}
            </button>
          </div>
        </div>
      )}

      {/* Create outlet member modal */}
      {showCreateOutletMember && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, boxSizing: 'border-box' }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 28, width: 440, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.15)', boxSizing: 'border-box' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#111827' }}>Add Staff to {selectedOutlet?.name}</h3>
              <button onClick={() => setShowCreateOutletMember(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}><X size={18} /></button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 5, textTransform: 'uppercase' as const, letterSpacing: '0.04em' }}>Full Name</label>
                <input style={inputStyle} placeholder="John Doe" value={outletMemberForm.name} onChange={e => setOutletMemberForm(f => ({ ...f, name: e.target.value }))} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 5, textTransform: 'uppercase' as const, letterSpacing: '0.04em' }}>Email</label>
                <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #e5e7eb', borderRadius: 6, overflow: 'hidden' }}>
                  <input style={{ ...inputStyle, border: 'none', borderRadius: 0, flex: 1 }} placeholder="john"
                    value={outletMemberForm.email_prefix}
                    onChange={e => setOutletMemberForm(f => ({ ...f, email_prefix: e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g, '') }))} />
                  <span style={{ padding: '0 12px', fontSize: 12, color: '#6b7280', background: '#f8fafc', borderLeft: '1px solid #e5e7eb', whiteSpace: 'nowrap' }}>@cloudairambo.com</span>
                </div>
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 5, textTransform: 'uppercase' as const, letterSpacing: '0.04em' }}>Password</label>
                <input style={inputStyle} type="password" placeholder="Min 6 characters" value={outletMemberForm.password} onChange={e => setOutletMemberForm(f => ({ ...f, password: e.target.value }))} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 5, textTransform: 'uppercase' as const, letterSpacing: '0.04em' }}>Role</label>
                <select value={outletMemberForm.role} onChange={e => setOutletMemberForm(f => ({ ...f, role: e.target.value }))} style={inputStyle}>
                  <option value="cashier">Cashier — POS only</option>
                  <option value="viewer">Viewer — read only</option>
                  {canManage && <option value="staff">Staff — Generator + Products + POS</option>}
                  {canManage && <option value="manager">Manager</option>}
                </select>
              </div>
              {/* Show default permissions preview */}
              <div style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 8, padding: '10px 12px' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase' as const, letterSpacing: '0.06em', marginBottom: 6 }}>Default permissions for {outletMemberForm.role}</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {Object.entries(ROLE_PERMISSIONS[outletMemberForm.role as Role] || {})
                    .filter(([, v]) => v)
                    .map(([k]) => (
                      <span key={k} style={{ fontSize: 9, background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 4, padding: '2px 6px', fontWeight: 600 }}>
                        {PERM_LABELS.find(p => p.key === k)?.label || k}
                      </span>
                    ))}
                </div>
              </div>
              {outletMemberError && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#dc2626', fontSize: 13 }}>
                  <AlertCircle size={14} /> {outletMemberError}
                </div>
              )}
              <button onClick={handleCreateOutletMember} disabled={creatingOutletMember} style={{ padding: 10, borderRadius: 8, border: 'none', background: 'linear-gradient(to right,#3b82f6,#9333ea)', color: '#fff', fontWeight: 600, fontSize: 14, cursor: creatingOutletMember ? 'not-allowed' : 'pointer', opacity: creatingOutletMember ? 0.7 : 1 }}>
                {creatingOutletMember ? 'Creating...' : '➕ Create & Assign to Outlet'}
              </button>
            </div>
          </div>
        </div>
      )}
    {/* Create outlet modal */}
      {showCreateOutlet && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, boxSizing: 'border-box' }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 28, width: 440, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.15)', boxSizing: 'border-box' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#111827' }}>Create Outlet</h3>
              <button onClick={() => setShowCreateOutlet(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}><X size={18} /></button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {[
                { label: 'Outlet Name', key: 'name', placeholder: 'Mumbai Branch' },
                { label: 'City', key: 'city', placeholder: 'Mumbai' },
              ].map(f => (
                <div key={f.key}>
                  <label style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' as const, letterSpacing: '0.06em', display: 'block', marginBottom: 4 }}>{f.label}</label>
                  <input style={{ ...inputStyle, ...(f.key === 'name' && isDuplicateOutletName ? { border: '1px solid #fecaca', background: '#fff5f5' } : {}) }} placeholder={f.placeholder} value={(outletForm as any)[f.key]}
                    onChange={e => setOutletForm(p => ({ ...p, [f.key]: e.target.value }))} />
                  {f.key === 'name' && isDuplicateOutletName && (
                    <div style={{ fontSize: 11, color: '#dc2626', marginTop: 4, fontWeight: 600 }}>
                      ⚠️ An outlet named "{outletForm.name.trim()}" already exists in your organization.
                    </div>
                  )}
                </div>
              ))}
              <div style={{ fontSize: 10, color: '#9ca3af', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '8px 10px' }}>
                🧾 An invoice/receipt prefix will be assigned automatically based on the outlet name (e.g. "Mumbai" → MUM-REC-00001).
              </div>

              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' as const, letterSpacing: '0.06em', display: 'block', marginBottom: 4 }}>
                  Which module(s) is this outlet for?
                </label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, border: '1px solid #e5e7eb', borderRadius: 6, padding: '8px 10px' }}>
                  {[
                    { id: 'suite', label: '💎 Suite Plan (this account)', active: true },
                    { id: 'ledger', label: '📒 Credit Book', active: hasLedger },
                    { id: 'queue', label: '🎫 Queue', active: hasQueue },
                    { id: 'employees', label: '👥 Employees', active: hasEmployees },
                    { id: 'gym', label: '🏋️ Fitness Center', active: hasGym },
                    { id: 'canteen', label: '🍽️ Meal Plans & Dining', active: hasCanteen },
                    { id: 'pos', label: '🧾 POS', active: hasPos },
                  ].filter(m => m.active).map(m => {
                    const isSuite = m.id === 'suite';
                    const isQueue = m.id === 'queue';
                    const isTrial = !isSuite && moduleStatuses[m.id as ModuleId] === 'trial';
                    // Queue never blocks new outlet creation — matches the
                    // backend and handleCreateOutlet's validation above.
                    // Each outlet buys its own counter allowance separately
                    // after creation, so there's no flat outlet cap here.
                    const cap = isSuite ? 1 : (isTrial ? Infinity : (moduleOutletCounts[m.id] ?? 1));
                    const used = isSuite
                      ? outlets.filter(o => !!o.suite_tier).length
                      : outlets.filter(o => o.modules?.includes(m.id)).length;
                    const isChecked = outletForm.modules.includes(m.id);
                    const isFull = !isChecked && used >= cap;
                    return (
                      <label key={m.id} style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                        fontSize: 13, color: isFull ? '#9ca3af' : '#374151',
                        cursor: isFull ? 'not-allowed' : 'pointer', opacity: isFull ? 0.6 : 1,
                      }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <input
                            type="radio"
                            name="create-outlet-module"
                            checked={isChecked}
                            disabled={isFull}
                            onChange={() => {
                              if (isFull) return;
                              setOutletForm(p => ({ ...p, modules: [m.id] }));
                            }}
                          />
                          {m.label}
                        </span>
                        <span style={{ fontSize: 10, fontWeight: 700, color: isFull ? '#dc2626' : '#94a3b8' }}>
                          {used}/{cap === Infinity ? '∞' : cap}{isFull ? ' · full' : ''}
                        </span>
                      </label>
                    );
                  })}
                </div>
                {outletForm.modules.length === 0 && (
                  <div style={{ fontSize: 11, color: '#dc2626', marginTop: 4 }}>
                    Required — this outlet won't count against any module's quota until assigned.
                  </div>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' as const, letterSpacing: '0.06em', display: 'block', marginBottom: 4 }}>Country</label>
                  <select
                    style={inputStyle}
                    value={outletForm.country}
                    onChange={e => {
                      const country = e.target.value;
                      setOutletForm(p => ({ ...p, country, region: '', currency: getCountryCurrency(country) }));
                    }}
                  >
                    <option value="">-- Select country --</option>
                    {ORDERED_COUNTRIES.map(c => (
                      <option key={c} value={c}>{COUNTRY_NAMES[c] || c} (+{COUNTRY_CODES[c]})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' as const, letterSpacing: '0.06em', display: 'block', marginBottom: 4 }}>Currency</label>
                  <select
                    style={inputStyle}
                    value={outletForm.currency}
                    onChange={e => setOutletForm(p => ({ ...p, currency: e.target.value }))}
                  >
                    <option value="">-- Select currency --</option>
                    {ALL_CURRENCIES.map(cur => (
                      <option key={cur} value={cur}>{cur} — {CURRENCY_MAP[cur]}</option>
                    ))}
                  </select>
                </div>
              </div>
              {outletForm.country && (getDefaultSchemeForCountryAndRegion(outletForm.country, outletForm.region).region_based || REGIONAL_TAX_COUNTRIES.has(outletForm.country)) && (
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' as const, letterSpacing: '0.06em', display: 'block', marginBottom: 4 }}>State / Province</label>
                  <StatePicker
                    countryCode={outletForm.country}
                    value={outletForm.region}
                    onChange={region => setOutletForm(p => ({ ...p, region }))}
                    placeholder="State / Province"
                    allowFlipUpward
                  />
                </div>
              )}
              {outletForm.country && (() => {
                const scheme = getDefaultSchemeForCountryAndRegion(outletForm.country, outletForm.region);
                return (
                  <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 10px', fontSize: 10, color: '#64748b' }}>
                    Tax scheme: <span style={{ fontWeight: 700, color: '#374151' }}>{scheme.label}</span> — drives the CGST/SGST/IGST-style breakdown on invoices and products.
                  </div>
                );
              })()}
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' as const, letterSpacing: '0.06em', display: 'block', marginBottom: 4 }}>
                  Tax ID <span style={{ color: '#cbd5e1', fontWeight: 400, textTransform: 'none' as const }}>optional — e.g. GSTIN, VAT No.</span>
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    style={{ ...inputStyle, fontFamily: 'monospace', textTransform: 'uppercase', letterSpacing: 0.5, paddingRight: 70 }}
                    placeholder="Tax registration number"
                    maxLength={25}
                    value={outletForm.tax_id}
                    onChange={e => setOutletForm(p => ({ ...p, tax_id: e.target.value.replace(/[\s\-\.]/g, '').toUpperCase() }))}
                  />
                  {outletForm.tax_id.length > 0 && (() => {
                    const meta = inspectTaxId(outletForm.tax_id, outletForm.country);
                    return (
                      <div style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                        {meta.isComplete || meta.currentLength >= meta.expectedLength ? (
                          <span style={{ fontSize: 11, fontWeight: 800, color: meta.isValid ? '#16a34a' : '#ef4444' }}>
                            {meta.isValid ? '✓ Valid' : '✗ Invalid'}
                          </span>
                        ) : (
                          <span style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af' }}>{meta.currentLength}/{meta.expectedLength}</span>
                        )}
                      </div>
                    );
                  })()}
                </div>
                {outletForm.tax_id.length >= 2 && (() => {
                  const meta = inspectTaxId(outletForm.tax_id, outletForm.country);
                  return (
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 6, marginTop: 6,
                      background: (meta.isComplete && !meta.isValid) ? '#fef2f2' : meta.isValid ? '#f0fdf4' : '#eff6ff',
                      border: `1px solid ${(meta.isComplete && !meta.isValid) ? '#fecaca' : meta.isValid ? '#bbf7d0' : '#bfdbfe'}`,
                      borderRadius: 8, padding: '6px 10px', fontSize: 11,
                    }}>
                      <span style={{ fontSize: 13 }}>{meta.countryFlag}</span>
                      <span style={{ fontWeight: 700, color: '#374151', flex: 1 }}>
                        {meta.countryName}{meta.detectedRegion ? ` · ${meta.detectedRegion}` : ''} <span style={{ color: '#94a3b8', fontWeight: 500 }}>({meta.taxLabel})</span>
                      </span>
                      {meta.detectedRegion && meta.detectedRegion !== outletForm.region && (
                        <span style={{ fontSize: 9, fontWeight: 800, color: '#d97706' }}>⚠ ≠ selected state</span>
                      )}
                      {meta.detectedRegion && meta.detectedRegion === outletForm.region && (
                        <span style={{ fontSize: 9, fontWeight: 800, color: '#16a34a' }}>✓ Matches</span>
                      )}
                    </div>
                  );
                })()}
              </div>
              <button onClick={handleCreateOutlet} disabled={creatingOutlet || outletForm.modules.length === 0 || isDuplicateOutletName} style={{ padding: 10, borderRadius: 8, border: 'none', background: 'linear-gradient(to right,#f59e0b,#ef4444)', color: '#fff', fontWeight: 700, fontSize: 14, cursor: (creatingOutlet || outletForm.modules.length === 0 || isDuplicateOutletName) ? 'not-allowed' : 'pointer', opacity: (creatingOutlet || outletForm.modules.length === 0 || isDuplicateOutletName) ? 0.5 : 1 }}>
                {creatingOutlet ? 'Creating...' : isDuplicateOutletName ? 'Name already used' : '🏪 Create Outlet'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit outlet modal */}
      {showEditOutlet && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, boxSizing: 'border-box' }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 28, width: 440, maxWidth: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.15)', boxSizing: 'border-box' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#111827' }}>Edit Outlet</h3>
              <button onClick={() => setShowEditOutlet(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}><X size={18} /></button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' as const, letterSpacing: '0.06em', display: 'block', marginBottom: 4 }}>Outlet Name</label>
                <input style={inputStyle} placeholder="Mumbai Branch" value={editOutletForm.name}
                  onChange={e => setEditOutletForm(p => ({ ...p, name: e.target.value }))} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' as const, letterSpacing: '0.06em', display: 'block', marginBottom: 4 }}>City</label>
                <input style={inputStyle} placeholder="Mumbai" value={editOutletForm.city}
                  onChange={e => setEditOutletForm(p => ({ ...p, city: e.target.value }))} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' as const, letterSpacing: '0.06em', display: 'block', marginBottom: 4 }}>Country</label>
                  <select
                    style={inputStyle}
                    value={editOutletForm.country}
                    onChange={e => {
                      const country = e.target.value;
                      setEditOutletForm(p => ({ ...p, country, region: '', currency: getCountryCurrency(country) }));
                    }}
                  >
                    <option value="">-- Select country --</option>
                    {ORDERED_COUNTRIES.map(c => (
                      <option key={c} value={c}>{COUNTRY_NAMES[c] || c} (+{COUNTRY_CODES[c]})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' as const, letterSpacing: '0.06em', display: 'block', marginBottom: 4 }}>Currency</label>
                  <select
                    style={inputStyle}
                    value={editOutletForm.currency}
                    onChange={e => setEditOutletForm(p => ({ ...p, currency: e.target.value }))}
                  >
                    <option value="">-- Select currency --</option>
                    {ALL_CURRENCIES.map(cur => (
                      <option key={cur} value={cur}>{cur} — {CURRENCY_MAP[cur]}</option>
                    ))}
                  </select>
                </div>
              </div>
              {editOutletForm.country && (getDefaultSchemeForCountryAndRegion(editOutletForm.country, editOutletForm.region).region_based || REGIONAL_TAX_COUNTRIES.has(editOutletForm.country)) && (
                <div>
                  <label style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' as const, letterSpacing: '0.06em', display: 'block', marginBottom: 4 }}>State / Province</label>
                  <StatePicker
                    countryCode={editOutletForm.country}
                    value={editOutletForm.region}
                    onChange={region => setEditOutletForm(p => ({ ...p, region }))}
                    placeholder="State / Province"
                    allowFlipUpward
                  />
                </div>
              )}
              {editOutletForm.country && (() => {
                const scheme = getDefaultSchemeForCountryAndRegion(editOutletForm.country, editOutletForm.region);
                return (
                  <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 10px', fontSize: 10, color: '#64748b' }}>
                    Tax scheme: <span style={{ fontWeight: 700, color: '#374151' }}>{scheme.label}</span> — drives the CGST/SGST/IGST-style breakdown on invoices and products.
                  </div>
                );
              })()}
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' as const, letterSpacing: '0.06em', display: 'block', marginBottom: 4 }}>
                  Tax ID <span style={{ color: '#cbd5e1', fontWeight: 400, textTransform: 'none' as const }}>optional — e.g. GSTIN, VAT No.</span>
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    style={{ ...inputStyle, fontFamily: 'monospace', textTransform: 'uppercase', letterSpacing: 0.5, paddingRight: 70 }}
                    placeholder="Tax registration number"
                    maxLength={25}
                    value={editOutletForm.tax_id}
                    onChange={e => setEditOutletForm(p => ({ ...p, tax_id: e.target.value.replace(/[\s\-\.]/g, '').toUpperCase() }))}
                  />
                  {editOutletForm.tax_id.length > 0 && (() => {
                    const meta = inspectTaxId(editOutletForm.tax_id, editOutletForm.country);
                    return (
                      <div style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                        {meta.isComplete || meta.currentLength >= meta.expectedLength ? (
                          <span style={{ fontSize: 11, fontWeight: 800, color: meta.isValid ? '#16a34a' : '#ef4444' }}>
                            {meta.isValid ? '✓ Valid' : '✗ Invalid'}
                          </span>
                        ) : (
                          <span style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af' }}>{meta.currentLength}/{meta.expectedLength}</span>
                        )}
                      </div>
                    );
                  })()}
                </div>
                {editOutletForm.tax_id.length >= 2 && (() => {
                  const meta = inspectTaxId(editOutletForm.tax_id, editOutletForm.country);
                  return (
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 6, marginTop: 6,
                      background: (meta.isComplete && !meta.isValid) ? '#fef2f2' : meta.isValid ? '#f0fdf4' : '#eff6ff',
                      border: `1px solid ${(meta.isComplete && !meta.isValid) ? '#fecaca' : meta.isValid ? '#bbf7d0' : '#bfdbfe'}`,
                      borderRadius: 8, padding: '6px 10px', fontSize: 11,
                    }}>
                      <span style={{ fontSize: 13 }}>{meta.countryFlag}</span>
                      <span style={{ fontWeight: 700, color: '#374151', flex: 1 }}>
                        {meta.countryName}{meta.detectedRegion ? ` · ${meta.detectedRegion}` : ''} <span style={{ color: '#94a3b8', fontWeight: 500 }}>({meta.taxLabel})</span>
                      </span>
                      {meta.detectedRegion && meta.detectedRegion !== editOutletForm.region && (
                        <span style={{ fontSize: 9, fontWeight: 800, color: '#d97706' }}>⚠ ≠ selected state</span>
                      )}
                      {meta.detectedRegion && meta.detectedRegion === editOutletForm.region && (
                        <span style={{ fontSize: 9, fontWeight: 800, color: '#16a34a' }}>✓ Matches</span>
                      )}
                    </div>
                  );
                })()}
              </div>
              <button onClick={handleSaveEditOutlet} disabled={savingEditOutlet} style={{ padding: 10, borderRadius: 8, border: 'none', background: 'linear-gradient(to right,#f59e0b,#ef4444)', color: '#fff', fontWeight: 700, fontSize: 14, cursor: savingEditOutlet ? 'not-allowed' : 'pointer', opacity: savingEditOutlet ? 0.6 : 1 }}>
                {savingEditOutlet ? 'Saving...' : '💾 Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
    </div>
    </>
  );
}
